import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {forkJoin, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {Book} from '../../../../../../model/Book';
import {Draft} from '../../../../../../model/Draft';
import {BookService} from '../../../../../service-data/book.service';
import {DraftService} from '../../../../../service-data/draft.service';
import {
  CreateProjectRequest,
  CreateProjectWarning,
  ProjectService
} from '../../../../../service-data/project.service';
import {StartupService} from '../../../../../service-ui/startup.service';
import {isOrganizableBook, isOrganizableDraft} from '../../../../../service-ui/orphan-items';

/**
 * A single item participating in the analysis — Book or Draft —
 * with all fields the scoring functions need pre-extracted into a
 * uniform shape.
 */
interface Extracted {
  key: string;               // 'B-<id>' or 'D-<id>'
  kind: 'book' | 'draft';
  ref: Book | Draft;
  id: number;
  title: string;             // Extracted (regex-matched) title used for all matching / suggested name logic.
  /**
   * Full trimmed title as originally stored on the Book / Draft.
   * Kept so the UI can render the entire title while visually
   * dimming the portion that doesn't participate in matching.
   * Equal to `title` when no regex is set or the regex didn't
   * match (in which case the "matched" portion is the whole
   * string and nothing is dimmed).
   */
  titleRaw: string;
  /**
   * Half-open index range `[titleMatchStart, titleMatchEnd)` into
   * `titleRaw` identifying the substring that participates in
   * matching. When no regex is set, this spans the entire raw
   * title so the template renders it uniformly with no dim
   * segments.
   */
  titleMatchStart: number;
  titleMatchEnd: number;
  coverResourceId: number;
  bodyResourceId: number;
  width: number;
  height: number;
  paperTypeId: number;
  bindingType: string;
  laminateType: string;
  lastUpdate: Date;
  bestResourceId: number;    // max(cover, body) — primary-suggestion tiebreak; see suggestPrimary().
  assigned: boolean;         // true when the item's work_ID > 0.
  workId: number;
}

/**
 * A proposed grouping surfaced to the user. May become a Project
 * on save, or add its items into an existing Project when the
 * scoring merged in an already-assigned Book / Draft (see
 * `targetProject_ID`).
 */
interface ProposedGroup {
  id: string;
  bookKeys: string[];        // stable identifiers into `this.itemsByKey`.
  draftKeys: string[];
  reasons: string[];         // human-readable match reasons, most important first.
  reasonStrengths: number[]; // parallel to `reasons` — for visual emphasis.
  suggestedPrimaryBookKey: string | null;
  suggestedPrimaryDraftKey: string | null;
  userPrimaryBookKey: string | null;   // operator override; null → keep suggestion.
  userPrimaryDraftKey: string | null;
  accepted: boolean;         // once true, items hide from other groups.
  saving: boolean;
  saved: boolean;
  saveError: string;
  manuallyRemovedKeys: Set<string>;

  /**
   * When non-null, the analysis matched the group's items against
   * a Book / Draft that is ALREADY a member of an existing
   * Project. Saving this group moves the group's items into that
   * Project instead of creating a new one.
   *
   * Historically (before 2026-08-06) the target-existing flow was
   * strictly "additive" — the existing Project's own composition
   * was left untouched. The new `suggestExistingProjectChanges`
   * option opens this up: when it's on, the anchors (items
   * already in the target Project) are included in
   * `bookKeys` / `draftKeys` and appear in `anchorKeys`, so the
   * operator can also re-primary the existing Project through
   * this UI. The `originalPrimaryBookKey` / `originalPrimaryDraftKey`
   * fields track the Project's current primaries so we can
   * highlight the diff and issue `setBookAsPrimary` /
   * `setDraftAsPrimary` calls on save.
   */
  targetProject_ID: number | null;
  /** Display name of the existing Project, when `targetProject_ID` is set. */
  targetProjectName: string | null;
  /**
   * Counts of items ALREADY on the target Project, shown in the
   * group header to give the operator context ("Adding 2 to
   * Project 'Foo' (already has 3 books, 1 draft)"). Purely
   * informational — the group's item list only contains the
   * proposed additions.
   */
  targetExistingBookCount: number;
  targetExistingDraftCount: number;

  /**
   * Keys of the Books / Drafts that ALREADY live on
   * `targetProject_ID` and are rendered in the read-only
   * "Already in this Project" section beneath the proposed
   * additions. Populated for every target-existing /
   * reassignment group so the operator can see what the Project
   * holds today before agreeing to add more to it.
   *
   * Anchors that are already surfaced inside the group's own
   * item list (i.e. when `suggestExistingProjectChanges` is on)
   * are deliberately EXCLUDED here so nothing is listed twice —
   * those rows already carry the "In this Project" chip.
   *
   * These keys are display-only: they never participate in
   * `visibleItemCount()`, the attribute analysis, the primary
   * suggestion, or the save payload.
   */
  existingBookKeys: string[];
  existingDraftKeys: string[];

  /** Collapsed state of the "Already in this Project" section. */
  existingCollapsed: boolean;

  /**
   * The target Project's CURRENT primary Book / Draft IDs (0 when
   * unset / not applicable). Used purely to paint a "Primary"
   * chip on the matching existing-member row so the operator can
   * see which of the Project's current members carries the
   * primary role. Distinct from `originalPrimaryBookKey` /
   * `originalPrimaryDraftKey`, which only get populated when the
   * flow is allowed to CHANGE the primary.
   */
  targetPrimaryBook_ID: number;
  targetPrimaryDraft_ID: number;

  /**
   * Keys already assigned to `targetProject_ID` at the time of
   * analysis. Empty when the target is `null`, or when
   * `suggestExistingProjectChanges` is off (in which case anchors
   * aren't surfaced in the item list at all). Used by the
   * template to paint anchors with an "existing member" chip so
   * the operator can tell them apart from proposed additions.
   */
  anchorKeys: Set<string>;

  /**
   * Current primary Book / Draft keys of the target Project, as
   * of the analysis pass. Null when the target is `null`, when
   * the current primary isn't in the extracted item pool, or when
   * `suggestExistingProjectChanges` is off. Used to detect and
   * highlight a proposed primary change, and to trigger the
   * `setBookAsPrimary` / `setDraftAsPrimary` calls on save.
   */
  originalPrimaryBookKey: string | null;
  originalPrimaryDraftKey: string | null;

  /**
   * Per-key origin metadata for items that would be MOVED across
   * Projects on save (only populated when `allowReassignment` is
   * on and the component spans more than one existing Project).
   * The template surfaces a "Moving from <Project>" chip on each
   * affected item so cross-project moves are unmistakable.
   */
  crossProjectMoves: Map<string, { fromProjectId: number; fromProjectName: string }>;

  /**
   * True when this group represents a full reassignment across
   * multiple existing Projects (only produced when
   * `allowReassignment` is on). Distinct visual treatment in the
   * template so the operator knows they're not merely adding
   * orphans — they are re-shuffling assignments.
   */
  isReassignment: boolean;

  /**
   * Editable project name shown in the group header. Initialized
   * from the primary Book / Draft title (and appended with the
   * binding type when `requireSameBinding` is enabled). Left in
   * sync with the primary / binding when `userEditedName` is
   * false; frozen at whatever the operator typed once
   * `userEditedName` flips to true. Only relevant for
   * new-Project groups (`targetProject_ID == null`).
   */
  proposedName: string;
  /** True once the operator has typed into the name field. */
  userEditedName: boolean;
}

/**
 * A single attribute tally across the visible items of a group.
 * `mode` is the most common value; `unanimous` is true when every
 * item that has the attribute agrees on that value; `hasValue` is
 * true when at least one item has a non-empty value for it.
 */
interface AttrTally {
  mode: any;
  unanimous: boolean;
  hasValue: boolean;
}

/**
 * Per-group attribute analysis used to color meta chips and to
 * render the "differences" strip in the group header. Same shape
 * as the "reasons" strip but semantically inverted — reasons say
 * why items belong together (cool/light), differences say what
 * still varies inside the group (warm) so the operator can spot
 * whether the grouping is really as tight as it looks.
 */
interface GroupAttrAnalysis {
  cover: AttrTally;
  body: AttrTally;
  trim: AttrTally;
  binding: AttrTally;
  paper: AttrTally;
  laminate: AttrTally;
  title: AttrTally;
  /** Human-readable summary of the attributes that differ, most-impactful first. */
  differences: string[];
}

/** Attribute keys valid for `attrMatchClass()` calls from the template. */
type AttrKey = 'cover' | 'body' | 'trim' | 'binding' | 'paper' | 'laminate' | 'title';

@Component({
  selector: 'app-organize-projects',
  templateUrl: './organize-projects.component.html',
  styleUrls: ['./organize-projects.component.css']
})
export class OrganizeProjectsComponent implements OnInit {
  // ── Analysis options (bound to the dialog's controls) ──

  /**
   * How much weight to give fuzzy title similarity, 0–100. At 0
   * only exact-title matches count; at 100 fuzzy similarity is as
   * strong as a matching resource_ID. Some orgs use disciplined
   * per-title naming conventions where similarity is meaningful —
   * others don't, and the operator can dial this down.
   */
  titleSimilarityWeight = 30;

  /**
   * Minimum title similarity percent (0–100) before the fuzzy
   * bonus is applied at all. Below the threshold two titles are
   * treated as unrelated no matter how it's scored.
   */
  titleSimilarityThreshold = 70;

  /**
   * When false (default), items already assigned to a Project are
   * excluded from the analysis so heal-style organizing doesn't
   * churn already-organized data. When true, they're included and
   * flagged with an "already in Project" badge.
   */
  considerAssigned = false;

  /**
   * When true (default false), each existing Project that shows
   * up in the analysis is opened up to CHANGES — the operator can
   * re-primary it, and the anchors (items already in that
   * Project) become visible alongside the proposed additions.
   * When false the existing-Project flow stays strictly additive:
   * anchors are not surfaced, primaries are left alone.
   *
   * This is deliberately separate from `considerAssigned`. The
   * latter merely governs whether add-to-existing proposals are
   * generated at all; this one opens the door to modifying the
   * existing Project's shape (primary Book / primary Draft).
   */
  suggestExistingProjectChanges = false;

  /**
   * When true (default false), components that span TWO OR MORE
   * existing Projects are surfaced as full reassignment
   * proposals — items get moved into a single target Project
   * (chosen as the Project with the most items in the
   * component) and cross-Project moves are highlighted with a
   * "Moving from <old Project>" chip. Without this flag such
   * components are skipped as ambiguous.
   *
   * This is the strongest option in the panel: it can rewire
   * arbitrary Book / Draft → Project assignments, so the default
   * is off and the analyze / save code paths make the resulting
   * changes obvious with distinct styling.
   */
  allowReassignment = false;

  /**
   * Restrict the pairwise scorer to a subset of the available
   * signals. Operators use this when they've already decided which
   * dimension "really" identifies a Project in their catalogue and
   * don't want the other dimensions muddying the groupings:
   *
   * - `'all'` (default): every signal in `scorePair` participates
   *   — cover / body resource matches, exact + fuzzy title matches,
   *   and (gated on a strong signal) physical property bonuses.
   * - `'titleOnly'`: only exact + fuzzy title matches contribute
   *   to the score. Resource / physical-property signals are
   *   ignored. Use when titles are the source of truth and
   *   resource_IDs are noisy (e.g. every upload re-uploads the
   *   PDF and gets a new resource_ID).
   * - `'resourcesOnly'`: only cover / body resource_ID matches
   *   contribute. Title signals are ignored entirely. Use when
   *   titles are noisy (draft numbering, translations, "copy of…"
   *   prefixes) but the underlying PDF resource IDs are trusted.
   */
  matchMode: 'all' | 'titleOnly' | 'resourcesOnly' = 'all';

  /**
   * When true, two items may only join the same group if they
   * share the SAME non-empty `bindingType`. Items with unknown /
   * blank binding are never merged into a binding-required group.
   * Also causes `computeProposedName()` to append the binding
   * type to the suggested Project name, so parallel
   * hardcover/softcover editions of the same title propose two
   * separately-named Projects. Off by default.
   */
  requireSameBinding = false;

  /**
   * Minimum pairwise score to link two items into the same group.
   * 100 ≈ "at least one strong signal" (matching cover resource,
   * matching body resource, or exact title match).
   */
  scoreThreshold = 100;

  /**
   * DISPLAY-ONLY filter (default false): once a proposed group
   * has been saved it disappears from the list, so the dialog
   * always shows only what is still outstanding and the operator
   * can work top-to-bottom without re-reading rows they've
   * already dealt with. Turning it on keeps saved proposals
   * visible (blue "Saved" cards) for review.
   *
   * Deliberately NOT part of `optionsSnapshot()` — it changes
   * nothing about the analysis, so flipping it must not make the
   * Analyze button go "dirty" and imply a re-analysis is owed.
   * Saved groups also stay in `this.groups` rather than being
   * spliced out, because their items must keep shadowing the
   * same items in other groups (see `isItemHiddenInGroup()`).
   */
  showSavedGroups = false;

  /**
   * Optional regex applied to every raw `title` before it feeds
   * the analysis. Empty string (default) preserves the historical
   * behaviour of matching on the full title. When set:
   *   - The extracted portion (capture group 1 if present, else
   *     the whole match) replaces the raw title in `Extracted.title`
   *     so exact-title / fuzzy-title matching, difference chips,
   *     and the auto-suggested Project name all use the trimmed
   *     value.
   *   - Titles that don't match the pattern fall back to the full
   *     raw title. This keeps books with atypical naming from
   *     silently dropping out of the pool just because they don't
   *     conform to the operator's regex.
   *
   * Changing the value re-runs `analyze()` — the operator adjusts
   * the pattern, hits Analyze, and sees the resulting groupings.
   * The compiled `RegExp` is cached in
   * `compiledTitleRegex*` so extract time stays O(1) per item
   * rather than O(items) recompiles.
   */
  titleRegex = '';

  /**
   * Latest compilation error surfaced under the regex input. Shown
   * inline (red hint) instead of the neutral hint text when
   * non-empty. Cleared on every successful compile.
   */
  titleRegexError = '';

  private compiledTitleRegex: RegExp | null = null;
  private compiledTitleRegexSource = '';

  // ── Analysis output ──

  /**
   * The item pool driving the current analysis, keyed by the same
   * stable 'B-<id>' / 'D-<id>' identifier used inside groups.
   */
  itemsByKey: Map<string, Extracted> = new Map();

  /**
   * Reverse index: item key → list of group IDs it appears in.
   * Populated on every `analyze()`. Used to render "also in group
   * N" badges next to items that show up in multiple groups.
   */
  itemInGroups: Map<string, string[]> = new Map();

  groups: ProposedGroup[] = [];

  /** Set to true while `analyze()` is running so the UI can dim. */
  analyzing = false;

  /** Optional error surfaced by `analyze()` (unexpected). */
  analyzeError = '';

  /**
   * Serialized snapshot of every option the analysis reads,
   * captured at the end of the last successful `analyze()`.
   * Compared against the current `optionsSnapshot()` to drive
   * `analyzeIsDirty` — the Analyze button is only visually
   * "filled" (raised, primary color) when the operator has
   * changed something that would produce a different result.
   * Empty at start-up so the initial state is treated as "dirty"
   * and the button attracts attention on first open.
   */
  private lastAnalyzeSnapshot = '';

  /** Selected folder to place the newly-created Projects under. */
  selectedFolder = '/';

  /**
   * Cache of per-group attribute analysis, keyed by `group.id`.
   * Cleared by `invalidateAttrCache()` whenever the visible set of
   * items in ANY group might have changed (item removed / restored,
   * accepted toggled, groups rebuilt). Recomputed lazily by
   * `getAttrAnalysis()` on first access after invalidation.
   */
  private attrCache: Map<string, GroupAttrAnalysis> = new Map();

  constructor(
    private bookService: BookService,
    private draftService: DraftService,
    private projectService: ProjectService,
    private startupService: StartupService,
    private dialogRef: MatDialogRef<OrganizeProjectsComponent>,
    @Inject(MAT_DIALOG_DATA) data: any
  ) {
    if (data && typeof data.selectedFolder === 'string' && data.selectedFolder) {
      this.selectedFolder = data.selectedFolder;
    }
  }

  ngOnInit(): void {
    this.analyze();
  }

  // ───────────────────────────────────────────────────────────
  // Extraction & scoring
  // ───────────────────────────────────────────────────────────

  /**
   * @param ignoreExclusions When true, the archived / proof
   *   guards below are skipped. Used ONLY by
   *   `collectProjectMembers()` — a Book that is already a member
   *   of an existing Project must be listed in the read-only
   *   "Already in this Project" section even when it would never
   *   be eligible as a PROPOSAL, otherwise the section would
   *   silently misrepresent the Project's contents.
   */
  private extractBook(book: Book, ignoreExclusions: boolean = false): Extracted | null {
    if (!book) { return null; }
    // Exclude archived Books from Project-organization proposals.
    // Archived Books are ones the operator has already decided
    // shouldn't participate in day-to-day workflows, so surfacing
    // them here would only pollute the suggestions and risk
    // pulling a shelved title back into an active Project. The
    // authoritative filter is server-side (see outbox
    // `2026-08-06-organize-projects-exclude-archived-books.md`);
    // this client-side guard is a belt-and-braces defense so
    // older server builds behave correctly too. `Book.archived`
    // is a stringly-typed 'TRUE' | 'FALSE' — treat any
    // case-insensitive 'TRUE' as archived.
    if (!ignoreExclusions && book.archived && String(book.archived).toUpperCase() === 'TRUE') { return null; }
    // Exclude proof Books for the same reason — a proof is a
    // one-off physical sample created to review the item, not a
    // catalogue title, so it has no place in Project
    // organization proposals. `Book.isProof` is stringly-typed
    // 'TRUE' | 'FALSE'; case-insensitive 'TRUE' means proof.
    if (!ignoreExclusions && book.isProof && String(book.isProof).toUpperCase() === 'TRUE') { return null; }
    const cover: any = book.cover;
    const body: any = book.body;
    const coverResourceId = cover && typeof cover.resource_ID === 'number' ? cover.resource_ID : 0;
    const bodyResourceId = body && typeof body.resource_ID === 'number' ? body.resource_ID : 0;
    const titleInfo = this.extractTitleInfo(book.title);
    return {
      key: 'B-' + book.ID,
      kind: 'book',
      ref: book,
      id: book.ID,
      title: titleInfo.title,
      titleRaw: titleInfo.raw,
      titleMatchStart: titleInfo.start,
      titleMatchEnd: titleInfo.end,
      coverResourceId,
      bodyResourceId,
      width: body && typeof body.width === 'number' ? body.width : 0,
      height: body && typeof body.height === 'number' ? body.height : 0,
      paperTypeId: body && typeof body.paperType_ID === 'number' ? body.paperType_ID : 0,
      bindingType: book.bindingType || '',
      laminateType: cover && typeof cover.laminateType === 'string' ? cover.laminateType : '',
      lastUpdate: book.lastUpdate ? new Date(book.lastUpdate) : new Date(0),
      bestResourceId: Math.max(coverResourceId, bodyResourceId),
      assigned: !!book.work_ID && book.work_ID > 0,
      workId: book.work_ID || 0
    };
  }

  private extractDraft(draft: Draft): Extracted | null {
    if (!draft) { return null; }
    const cover: any = draft.draftCover;
    const body: any = draft.draftBody;
    const coverResourceId = cover && typeof cover.resource_ID === 'number' ? cover.resource_ID : 0;
    const bodyResourceId = body && typeof body.resource_ID === 'number' ? body.resource_ID : 0;
    const titleInfo = this.extractTitleInfo(draft.title);
    return {
      key: 'D-' + draft.ID,
      kind: 'draft',
      ref: draft,
      id: draft.ID,
      title: titleInfo.title,
      titleRaw: titleInfo.raw,
      titleMatchStart: titleInfo.start,
      titleMatchEnd: titleInfo.end,
      coverResourceId,
      bodyResourceId,
      width: body && typeof body.width === 'number' ? body.width : 0,
      height: body && typeof body.height === 'number' ? body.height : 0,
      paperTypeId: body && typeof body.paperType_ID === 'number' ? body.paperType_ID : 0,
      bindingType: draft.bindingType || '',
      laminateType: cover && typeof cover.laminateType === 'string' ? cover.laminateType : '',
      lastUpdate: draft.lastUpdate ? new Date(draft.lastUpdate) : new Date(0),
      bestResourceId: Math.max(coverResourceId, bodyResourceId),
      assigned: !!draft.work_ID && draft.work_ID > 0,
      workId: draft.work_ID || 0
    };
  }

  /**
   * Resolve a Project's current primary Book / Draft ID from
   * whichever shape the ProjectService cache happens to carry
   * (`primary*_ID` scalar, or the linked `primary*` object).
   * Returns 0 when unset / unknown.
   */
  private projectPrimaryId(project: any, kind: 'book' | 'draft'): number {
    if (!project) { return 0; }
    const raw = kind === 'book'
      ? (project.primaryBook_ID || (project.primaryBook && project.primaryBook.ID))
      : (project.primaryDraft_ID || (project.primaryDraft && project.primaryDraft.ID));
    const n = Number(raw);
    return n > 0 ? n : 0;
  }

  /**
   * Enumerate every Book / Draft that currently belongs to
   * `projectId`, returning stable item keys ready for the
   * template. Ensures each member has an entry in `itemsByKey`
   * (extracting it on demand when the analysis pool skipped it)
   * so `getItem(key)` resolves in the "Already in this Project"
   * section.
   *
   * Two sources are unioned, deliberately:
   *  1. `project.books` / `project.drafts` — populated by the
   *     ProjectService's `getLinkData()` `AddUpdateArray` links.
   *  2. A sweep of the Book / Draft caches for `work_ID ===
   *     projectId` — catches members whose link arrays haven't
   *     been rebuilt yet (e.g. a move applied earlier in this
   *     same dialog session) and members the Project row simply
   *     doesn't carry.
   *
   * Exclusions (archived / proof) are intentionally bypassed
   * here: these rows describe what the Project ACTUALLY holds,
   * not what may be proposed, so hiding members would be
   * misleading.
   *
   * Result lists are sorted by title (then ID) so repeated
   * analyses render in a stable, scannable order.
   */
  private collectProjectMembers(projectId: number, project: any): { bookKeys: string[]; draftKeys: string[] } {
    const bookKeys: string[] = [];
    const draftKeys: string[] = [];
    const seen = new Set<string>();

    const addBook = (b: Book): void => {
      if (!b || !b.ID) { return; }
      const key = 'B-' + b.ID;
      if (seen.has(key)) { return; }
      seen.add(key);
      if (!this.itemsByKey.has(key)) {
        const ext = this.extractBook(b, true);
        if (!ext) { return; }
        this.itemsByKey.set(key, ext);
      }
      bookKeys.push(key);
    };
    const addDraft = (d: Draft): void => {
      if (!d || !d.ID) { return; }
      const key = 'D-' + d.ID;
      if (seen.has(key)) { return; }
      seen.add(key);
      if (!this.itemsByKey.has(key)) {
        const ext = this.extractDraft(d);
        if (!ext) { return; }
        this.itemsByKey.set(key, ext);
      }
      draftKeys.push(key);
    };

    if (project && Array.isArray(project.books)) {
      for (const b of project.books) { addBook(b); }
    }
    if (project && Array.isArray(project.drafts)) {
      for (const d of project.drafts) { addDraft(d); }
    }
    for (const b of (this.bookService.objects || [])) {
      if (b && Number(b.work_ID) === Number(projectId)) { addBook(b); }
    }
    for (const d of (this.draftService.objects || [])) {
      if (d && Number(d.work_ID) === Number(projectId)) { addDraft(d); }
    }

    const byTitle = (x: string, y: string): number => {
      const ix = this.itemsByKey.get(x);
      const iy = this.itemsByKey.get(y);
      const tx = ((ix && ix.titleRaw) || '').toLowerCase();
      const ty = ((iy && iy.titleRaw) || '').toLowerCase();
      const cmp = tx.localeCompare(ty);
      if (cmp !== 0) { return cmp; }
      return ((ix && ix.id) || 0) - ((iy && iy.id) || 0);
    };
    bookKeys.sort(byTitle);
    draftKeys.sort(byTitle);
    return { bookKeys, draftKeys };
  }

  /**
   * Apply the operator's optional title regex to `rawTitle` and
   * return a small info bundle used by both the analysis
   * (`title` — the substring that will participate in matching
   * / become the default proposed Project name) and the UI
   * (`raw` plus a half-open `[start, end)` range into `raw`
   * so the template can render the whole title with the
   * unmatched portions visually dimmed).
   *
   * Behaviour matches the earlier `extractTitlePart`:
   *
   *  - Empty / whitespace-only pattern → the whole trimmed
   *    title is the "match" (range spans the entire string, so
   *    the template renders it uniformly with no dim segments).
   *  - Pattern with a capture group → capture group 1 becomes
   *    the match; range points at the capture group inside
   *    `raw`.
   *  - Pattern without a capture group → the whole regex match
   *    becomes the match; range points at the match inside
   *    `raw`.
   *  - Pattern that fails to compile → cache the error and fall
   *    back to the whole raw title as the "match".
   *  - Pattern that compiles but doesn't match this title → the
   *    whole raw title is the "match" (nothing is dimmed for
   *    items that don't fit the pattern — they still participate
   *    in analysis using their full title).
   *
   * The compiled `RegExp` is cached across items keyed by
   * source string so the extract path stays O(1) per item.
   */
  private extractTitleInfo(rawTitle: string): { title: string; raw: string; start: number; end: number } {
    const raw = (rawTitle || '').trim();
    const wholeAsMatch = { title: raw, raw, start: 0, end: raw.length };
    const pattern = (this.titleRegex || '').trim();
    if (!pattern) { return wholeAsMatch; }
    if (pattern !== this.compiledTitleRegexSource) {
      this.compiledTitleRegexSource = pattern;
      try {
        this.compiledTitleRegex = new RegExp(pattern);
        this.titleRegexError = '';
      } catch (e) {
        this.compiledTitleRegex = null;
        this.titleRegexError = (e && e.message) || 'Invalid regular expression.';
      }
    }
    if (!this.compiledTitleRegex) { return wholeAsMatch; }
    const execResult = this.compiledTitleRegex.exec(raw);
    if (!execResult) { return wholeAsMatch; }
    const fullMatch = execResult[0] || '';
    const fullMatchStart = execResult.index || 0;
    let extracted: string;
    let start: number;
    let end: number;
    if (execResult.length > 1 && execResult[1] != null) {
      extracted = execResult[1];
      // Locate the capture group's text inside the full match so
      // we can map it back to an absolute index within `raw`.
      // `String.prototype.indexOf` here is safe because the
      // capture is by definition a substring of the full match.
      const inMatchOffset = fullMatch.indexOf(extracted);
      start = fullMatchStart + (inMatchOffset >= 0 ? inMatchOffset : 0);
      end = start + extracted.length;
    } else {
      extracted = fullMatch;
      start = fullMatchStart;
      end = start + fullMatch.length;
    }
    const trimmed = (extracted || '').trim();
    // An empty extraction would collapse into a no-op matcher
    // and produce a blank auto-suggested Project name — fall
    // back to the whole title in that case.
    if (!trimmed) { return wholeAsMatch; }
    return { title: trimmed, raw, start, end };
  }

  /**
   * `[(ngModel)]`-style handler for the title regex text input.
   * Discards the cached compilation so the next `analyze()` picks
   * up the new pattern, and pre-validates the regex so the
   * inline error hint updates immediately (before the operator
   * clicks Analyze). No auto-analyze on every keystroke — the
   * operator is expected to click Analyze once they're happy
   * with the pattern, matching the behaviour of the other
   * analysis options.
   */
  onTitleRegexChange(value: string): void {
    this.titleRegex = value || '';
    // Force recompile on next extract.
    this.compiledTitleRegexSource = '';
    this.compiledTitleRegex = null;
    const pattern = this.titleRegex.trim();
    if (!pattern) { this.titleRegexError = ''; return; }
    try {
      // Compile eagerly so the error message shows up under the
      // input even before the operator hits Analyze.
      this.compiledTitleRegex = new RegExp(pattern);
      this.compiledTitleRegexSource = pattern;
      this.titleRegexError = '';
    } catch (e) {
      this.compiledTitleRegex = null;
      this.compiledTitleRegexSource = pattern;
      this.titleRegexError = (e && e.message) || 'Invalid regular expression.';
    }
  }

  /**
   * Score a pair of items and produce a list of human-readable
   * match reasons. Only signals that actually matched appear.
   *
   * Weight design (relative importance):
   * - Cover `resource_ID` match: 100 (very strong — same file).
   * - Body `resource_ID` match:  100 (very strong).
   * - Exact title match:         80  (strong for orgs that name
   *                                    consistently, harmless
   *                                    otherwise because it takes
   *                                    an actual string match).
   * - Fuzzy title similarity ≥ threshold: `similarity * weight`
   *                                    (operator-tunable).
   * - Physical property matches (size, binding, paper, laminate)
   *   are ONLY counted when at least one strong signal (≥ 100)
   *   is present. Two random books with the same trim size
   *   shouldn't be grouped.
   */
  private scorePair(a: Extracted, b: Extracted): { score: number; reasons: string[]; strengths: number[] } {
    const reasons: string[] = [];
    const strengths: number[] = [];
    let score = 0;
    let hasStrongSignal = false;

    // Hard binding gate. When the operator has required matching
    // binding types, refuse the edge outright unless both sides
    // declare the SAME non-empty binding. Empty / unknown binding
    // on either side is treated as a mismatch to avoid quietly
    // mixing items whose binding we can't verify. This runs before
    // any score accumulation so the pair never enters the
    // adjacency list at all — the resulting components partition
    // naturally along binding lines.
    if (this.requireSameBinding) {
      if (!a.bindingType || !b.bindingType || a.bindingType !== b.bindingType) {
        return { score: 0, reasons: [], strengths: [] };
      }
    }

    const useResources = this.matchMode !== 'titleOnly';
    const useTitle = this.matchMode !== 'resourcesOnly';

    // Cover resource match.
    if (useResources && a.coverResourceId > 0 && a.coverResourceId === b.coverResourceId) {
      score += 100;
      hasStrongSignal = true;
      reasons.push(`Same cover resource #${a.coverResourceId}`);
      strengths.push(100);
    }
    // Body resource match.
    if (useResources && a.bodyResourceId > 0 && a.bodyResourceId === b.bodyResourceId) {
      score += 100;
      hasStrongSignal = true;
      reasons.push(`Same body resource #${a.bodyResourceId}`);
      strengths.push(100);
    }
    // Exact title match (case-insensitive, trimmed).
    if (useTitle && a.title && b.title && a.title.toLowerCase() === b.title.toLowerCase()) {
      score += 80;
      hasStrongSignal = true;
      reasons.push(`Same title "${a.title}"`);
      strengths.push(80);
    } else if (useTitle && a.title && b.title && this.titleSimilarityWeight > 0) {
      // Fuzzy title similarity gated by the operator threshold.
      const sim = this.titleSimilarity(a.title, b.title);
      if (sim * 100 >= this.titleSimilarityThreshold) {
        const bonus = Math.round(sim * this.titleSimilarityWeight);
        if (bonus > 0) {
          score += bonus;
          // Consider a very-high fuzzy match as a strong signal so
          // shape/binding/laminate can also count.
          if (sim >= 0.9) { hasStrongSignal = true; }
          reasons.push(`Similar titles (${Math.round(sim * 100)}%)`);
          strengths.push(bonus);
        }
      }
    }

    if (hasStrongSignal) {
      if (a.width > 0 && a.height > 0 && this.approxEquals(a.width, b.width, 4) && this.approxEquals(a.height, b.height, 4)) {
        score += 15;
        reasons.push('Same trim size');
        strengths.push(15);
      }
      if (a.bindingType && a.bindingType === b.bindingType) {
        score += 10;
        reasons.push(`Same binding (${a.bindingType})`);
        strengths.push(10);
      }
      if (a.paperTypeId > 0 && a.paperTypeId === b.paperTypeId) {
        score += 10;
        reasons.push(`Same paper type`);
        strengths.push(10);
      }
      if (a.laminateType && a.laminateType === b.laminateType) {
        score += 10;
        reasons.push(`Same lamination (${a.laminateType})`);
        strengths.push(10);
      }
    }

    return { score, reasons, strengths };
  }

  private approxEquals(a: number, b: number, tolerance: number): boolean {
    return Math.abs(a - b) <= tolerance;
  }

  /**
   * Normalized (0–1) title similarity based on Levenshtein
   * distance divided by max(len). Cheap enough to be run on every
   * pair of items in `analyze()` for the sizes we see in practice.
   */
  private titleSimilarity(a: string, b: string): number {
    const s1 = a.toLowerCase();
    const s2 = b.toLowerCase();
    if (!s1.length && !s2.length) { return 1; }
    const max = Math.max(s1.length, s2.length);
    if (max === 0) { return 1; }
    const dist = this.levenshtein(s1, s2);
    return 1 - dist / max;
  }

  private levenshtein(a: string, b: string): number {
    const m = a.length;
    const n = b.length;
    if (m === 0) { return n; }
    if (n === 0) { return m; }
    // Two-row DP to keep memory small.
    let prev = new Array<number>(n + 1);
    let curr = new Array<number>(n + 1);
    for (let j = 0; j <= n; j++) { prev[j] = j; }
    for (let i = 1; i <= m; i++) {
      curr[0] = i;
      for (let j = 1; j <= n; j++) {
        const cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        curr[j] = Math.min(
          curr[j - 1] + 1,        // insert
          prev[j] + 1,            // delete
          prev[j - 1] + cost      // substitute
        );
      }
      // Swap rows.
      const tmp = prev; prev = curr; curr = tmp;
    }
    return prev[n];
  }

  // ───────────────────────────────────────────────────────────
  // Analysis pass
  // ───────────────────────────────────────────────────────────

  /**
   * Serialize every option the analysis reads into a stable
   * string. Used to detect whether the operator has changed
   * anything since the last successful `analyze()` — the Analyze
   * button de-emphasises itself when there's nothing new to
   * compute. Kept intentionally cheap; runs on every change
   * detection cycle via the `analyzeIsDirty` getter.
   */
  private optionsSnapshot(): string {
    return JSON.stringify([
      this.titleSimilarityWeight,
      this.titleSimilarityThreshold,
      this.titleRegex || '',
      this.considerAssigned,
      this.matchMode,
      this.requireSameBinding,
      this.scoreThreshold,
      this.suggestExistingProjectChanges,
      this.allowReassignment
    ]);
  }

  /**
   * True when the operator has changed any analysis option since
   * the last successful `analyze()`. Drives the Analyze button's
   * `color` / `mat-raised-button` vs `mat-stroked-button` split
   * in the template — the button is only "filled in" when a new
   * analyze would produce something different.
   */
  get analyzeIsDirty(): boolean {
    return this.optionsSnapshot() !== this.lastAnalyzeSnapshot;
  }

  /**
   * The groups actually rendered. Saved groups are filtered out
   * unless `showSavedGroups` is on — they stay in `this.groups`
   * so their reserved items keep shadowing the same items
   * elsewhere, they just aren't drawn.
   *
   * A saved group that carries a `saveError` is ALWAYS drawn.
   * Both save paths deliberately set `saved = true` on partial
   * failure (Project created but some items didn't move, primary
   * assignment rejected, …) and put the detail in `saveError`,
   * which is only rendered inside the group's own card. Filtering
   * those out would silently swallow the one message the operator
   * has to act on.
   */
  get visibleGroups(): ProposedGroup[] {
    if (this.showSavedGroups) { return this.groups; }
    return this.groups.filter(g => !this.isGroupFilteredOut(g));
  }

  /** True when `group` is being hidden purely by the saved-groups filter. */
  private isGroupFilteredOut(group: ProposedGroup): boolean {
    if (this.showSavedGroups) { return false; }
    return !!group && group.saved && !group.saveError;
  }

  /** Total number of groups that have been saved this session. */
  get savedGroupCount(): number {
    let n = 0;
    for (const g of this.groups) { if (g.saved) { n++; } }
    return n;
  }

  /**
   * How many saved groups are currently being hidden by the
   * `showSavedGroups` filter. Surfaced as a small note so the
   * disappearance of a card after Save is never mysterious.
   */
  get hiddenSavedGroupCount(): number {
    let n = 0;
    for (const g of this.groups) { if (this.isGroupFilteredOut(g)) { n++; } }
    return n;
  }

  /** `trackBy` for the groups `*ngFor` — keeps DOM stable across filter changes. */
  trackGroupById(index: number, group: ProposedGroup): string {
    return group ? group.id : String(index);
  }

  /** True when the given key is an anchor (existing member of the target Project). */
  isAnchor(group: ProposedGroup, key: string): boolean {
    return !!group && group.anchorKeys && group.anchorKeys.has(key);
  }

  /**
   * Number of read-only "already in this Project" rows rendered
   * for a group (existing members that aren't already shown as
   * anchors / additions in the main item list).
   */
  existingMemberCount(group: ProposedGroup): number {
    if (!group) { return 0; }
    return (group.existingBookKeys ? group.existingBookKeys.length : 0)
         + (group.existingDraftKeys ? group.existingDraftKeys.length : 0);
  }

  /** All display keys for the "Already in this Project" section, Books first. */
  existingMemberKeys(group: ProposedGroup): string[] {
    if (!group) { return []; }
    return (group.existingBookKeys || []).concat(group.existingDraftKeys || []);
  }

  /** Collapse / expand the read-only existing-members section. */
  toggleExistingMembers(group: ProposedGroup): void {
    if (!group) { return; }
    group.existingCollapsed = !group.existingCollapsed;
  }

  /**
   * True when `key` is the target Project's CURRENT primary Book
   * / Draft. Purely informational — it says what the Project
   * looks like today, independent of any primary change this
   * group may propose.
   */
  isTargetPrimary(group: ProposedGroup, key: string): boolean {
    if (!group || group.targetProject_ID == null) { return false; }
    const it = this.itemsByKey.get(key);
    if (!it) { return false; }
    return it.kind === 'book'
      ? (group.targetPrimaryBook_ID > 0 && group.targetPrimaryBook_ID === it.id)
      : (group.targetPrimaryDraft_ID > 0 && group.targetPrimaryDraft_ID === it.id);
  }

  /** True when the given key is being moved from a different existing Project. */
  isCrossProjectMove(group: ProposedGroup, key: string): boolean {
    return !!group && group.crossProjectMoves && group.crossProjectMoves.has(key);
  }

  /** Origin Project label for a cross-Project move, or '' when there is none. */
  originProjectName(group: ProposedGroup, key: string): string {
    const info = group && group.crossProjectMoves && group.crossProjectMoves.get(key);
    return info ? info.fromProjectName : '';
  }

  /**
   * True when the effective primary Book differs from the
   * existing Project's original primary Book — only meaningful
   * for target-existing groups when
   * `suggestExistingProjectChanges` (or reassignment) opened the
   * primary up to change. Drives the "primary change" highlight
   * on the group header.
   */
  hasPrimaryBookChange(group: ProposedGroup): boolean {
    if (!group || group.targetProject_ID == null) { return false; }
    if (!group.originalPrimaryBookKey) { return false; }
    const eff = this.getEffectivePrimaryBookKey(group);
    return !!eff && eff !== group.originalPrimaryBookKey;
  }

  hasPrimaryDraftChange(group: ProposedGroup): boolean {
    if (!group || group.targetProject_ID == null) { return false; }
    if (!group.originalPrimaryDraftKey) { return false; }
    const eff = this.getEffectivePrimaryDraftKey(group);
    return !!eff && eff !== group.originalPrimaryDraftKey;
  }

  /**
   * A one-item new-Project proposal.
   *
   * Used by `analyze()`'s backstop for an unfiled item the grouping pass
   * declined to place. It carries no match reasons because there is no
   * match to explain -- the only claim being made is that the item is not
   * in a Project, which is exactly what the banner told the operator.
   */
  private makeSingletonGroup(item: Extracted, id: string): ProposedGroup {
    const isBook = item.kind === 'book';
    const g: ProposedGroup = {
      id,
      bookKeys: isBook ? [item.key] : [],
      draftKeys: isBook ? [] : [item.key],
      reasons: [],
      reasonStrengths: [],
      suggestedPrimaryBookKey: isBook ? item.key : null,
      suggestedPrimaryDraftKey: isBook ? null : item.key,
      userPrimaryBookKey: null,
      userPrimaryDraftKey: null,
      accepted: false,
      saving: false,
      saved: false,
      saveError: '',
      manuallyRemovedKeys: new Set<string>(),
      targetProject_ID: null,
      targetProjectName: null,
      targetExistingBookCount: 0,
      targetExistingDraftCount: 0,
      existingBookKeys: [],
      existingDraftKeys: [],
      existingCollapsed: false,
      targetPrimaryBook_ID: 0,
      targetPrimaryDraft_ID: 0,
      anchorKeys: new Set<string>(),
      originalPrimaryBookKey: null,
      originalPrimaryDraftKey: null,
      crossProjectMoves: new Map<string, { fromProjectId: number; fromProjectName: string }>(),
      isReassignment: false,
      proposedName: '',
      userEditedName: false
    };
    g.proposedName = this.computeProposedName(g);
    return g;
  }

  analyze(): void {
    this.analyzing = true;
    this.analyzeError = '';
    try {
      // Extract eligible items. Assigned items are ALWAYS included
      // now — regardless of the `considerAssigned` toggle — so an
      // orphan Book / Draft that scores high with an already-
      // assigned sibling can be recognised as belonging to that
      // sibling's existing Project (see the classification pass
      // below). The `considerAssigned` toggle now only governs
      // whether groups with no orphan additions are surfaced at
      // all: with the toggle OFF (default) we suppress any group
      // that is entirely already-assigned items, keeping the
      // dialog focused on actionable proposals.
      const items: Extracted[] = [];
      // Eligibility is decided by `service-ui/orphan-items.ts` -- the same
      // module the "Items are not in a Project" banner counts with, so the
      // banner can never advertise work this pool has already discarded.
      //
      // Both caches survive an org or role switch and organizing across
      // Organizations is never valid, so Books AND Drafts are filtered by
      // the Organization in view. The draft path used to be exempt, on the
      // stated grounds that Drafts are "not org-scoped by `org_ID` on the
      // model". They are -- `Draft.org_ID` is declared on `model/Draft.ts`
      // -- and the exemption is what let a Draft from a previously-viewed
      // Organization sit in the banner's count indefinitely.
      const currentOrg = this.startupService && this.startupService.getCurrentOrganization && this.startupService.getCurrentOrganization();
      const currentOrgId = currentOrg && currentOrg.ID;
      for (const b of (this.bookService.objects || [])) {
        if (!isOrganizableBook(b, currentOrgId)) { continue; }
        const ext = this.extractBook(b);
        if (!ext) { continue; }
        items.push(ext);
      }
      for (const d of (this.draftService.objects || [])) {
        if (!isOrganizableDraft(d, currentOrgId)) { continue; }
        const ext = this.extractDraft(d);
        if (!ext) { continue; }
        items.push(ext);
      }

      // Rebuild the by-key index.
      this.itemsByKey = new Map();
      for (const it of items) { this.itemsByKey.set(it.key, it); }

      // Build a Project.ID → Project lookup so we can name the
      // target Project in group headers and count its existing
      // members. Falls back to a synthetic "Project #<id>" label
      // when the ProjectService's cache doesn't hold a matching
      // row (e.g. the Book's `work_ID` points at a Project the
      // operator can't see — shouldn't normally happen, but be
      // defensive).
      const projectById = new Map<number, any>();
      for (const p of (this.projectService.objects || [])) {
        if (p && typeof p.ID === 'number' && p.ID > 0) {
          projectById.set(p.ID, p);
        }
      }

      // Build an adjacency list of pairs that score above threshold.
      // The reasons for each edge are stashed so we can aggregate
      // them when we roll pairs up into a group.
      interface Edge { a: string; b: string; score: number; reasons: string[]; strengths: number[]; }
      const edges: Edge[] = [];
      for (let i = 0; i < items.length; i++) {
        for (let j = i + 1; j < items.length; j++) {
          const s = this.scorePair(items[i], items[j]);
          if (s.score >= this.scoreThreshold) {
            edges.push({ a: items[i].key, b: items[j].key, score: s.score, reasons: s.reasons, strengths: s.strengths });
          }
        }
      }

      // Union-find to compute connected components.
      const parent = new Map<string, string>();
      const find = (k: string): string => {
        let cur = k;
        let p = parent.get(cur);
        while (p !== undefined && p !== cur) {
          cur = p;
          p = parent.get(cur);
        }
        // Path compression.
        let q = k;
        let qp = parent.get(q);
        while (qp !== undefined && qp !== cur) {
          parent.set(q, cur);
          q = qp;
          qp = parent.get(q);
        }
        return cur;
      };
      const union = (x: string, y: string) => {
        const rx = find(x), ry = find(y);
        if (rx !== ry) { parent.set(rx, ry); }
      };
      for (const it of items) { parent.set(it.key, it.key); }
      for (const e of edges) { union(e.a, e.b); }

      // Bucket items by root; also aggregate edge reasons per bucket.
      const rootToKeys = new Map<string, string[]>();
      const rootToReasons = new Map<string, Map<string, number>>();
      for (const it of items) {
        const r = find(it.key);
        let bucket = rootToKeys.get(r);
        if (!bucket) {
          bucket = [];
          rootToKeys.set(r, bucket);
          rootToReasons.set(r, new Map<string, number>());
        }
        bucket.push(it.key);
      }
      for (const e of edges) {
        const r = find(e.a);
        const reasonMap = rootToReasons.get(r);
        if (!reasonMap) { continue; }
        for (let k = 0; k < e.reasons.length; k++) {
          const reason = e.reasons[k];
          const strength = e.strengths[k];
          const existing = reasonMap.get(reason);
          if (existing === undefined || existing < strength) {
            reasonMap.set(reason, strength);
          }
        }
      }

      // Build ProposedGroup objects. Flavours per component:
      //
      // 1. Component contains items already assigned to EXACTLY ONE
      //    existing Project → surface as an "add to <Project>"
      //    group. The group's item list holds the ORPHAN
      //    items in the component. When
      //    `suggestExistingProjectChanges` is on, the anchors
      //    (items already in the Project) are ALSO included in
      //    the item list and the primaries are recomputed so the
      //    operator can re-primary the existing Project.
      // 2. Component contains items already assigned to MORE THAN
      //    ONE existing Project. Without `allowReassignment` the
      //    group is skipped as ambiguous. With `allowReassignment`
      //    on, the group becomes a REASSIGNMENT proposal — a
      //    single target Project is chosen and cross-Project
      //    moves are highlighted per item.
      // 3. Component contains only orphan items → new-Project
      //    candidate, exactly as before.
      const newGroups: ProposedGroup[] = [];
      let groupCounter = 1;
      rootToKeys.forEach((keys, root) => {
        // Classify by the set of assigned Projects in the component.
        // Additionally, treat an item's title as a signal that it
        // belongs to an existing Project whose NAME matches that
        // title (exact or fuzzy) so orphaned Books / Drafts with a
        // matching Project title will be proposed to be added to
        // that Project even when no Book/Draft in the Project is
        // currently present in the analyzed pool.
        const assignedProjectIds = new Set<number>();
        const countByProject = new Map<number, number>();
        for (const k of keys) {
          const it = this.itemsByKey.get(k);
          if (it && it.assigned && it.workId > 0) {
            assignedProjectIds.add(it.workId);
            countByProject.set(it.workId, (countByProject.get(it.workId) || 0) + 1);
          }
        }

        // Title-to-Project name matching: if an item's extracted
        // title matches an existing Project's name (exact case-
        // insensitive match OR a fuzzy similarity above the operator
        // threshold) count that as evidence for assignment to that
        // Project. This allows orphaned items to be proposed for an
        // existing Project that is named after the title even when
        // the Project has no members present in the component.
        const useTitleMatchForProjects = this.matchMode !== 'resourcesOnly';
        // Track projects that matched by title so we can surface a
        // human-readable reason and optionally auto-accept the
        // proposal to add orphans into that existing Project.
        const titleMatchedProjectIds = new Set<number>();
        if (useTitleMatchForProjects) {
          for (const k of keys) {
            const it = this.itemsByKey.get(k);
            if (!it || !it.title) { continue; }
            // Skip items already counted by explicit assignment
            // above; we'll still allow additional matches to bump
            // the project's count if needed.
            for (const [pid, project] of projectById.entries()) {
              if (!project || !project.name) { continue; }
              const pname = String(project.name).trim();
              if (!pname) { continue; }
              // Exact title == project name (case-insensitive)
              if (it.title.toLowerCase() === pname.toLowerCase()) {
                assignedProjectIds.add(pid);
                countByProject.set(pid, (countByProject.get(pid) || 0) + 1);
                titleMatchedProjectIds.add(pid);
                // A strong exact match is sufficient — move to next item.
                break;
              }
              // Fuzzy project-name similarity (operator-tunable).
              if (this.titleSimilarityWeight > 0) {
                const sim = this.titleSimilarity(it.title, pname);
                if (sim * 100 >= this.titleSimilarityThreshold) {
                  // Treat a fuzzy title→project-name hit as evidence.
                  assignedProjectIds.add(pid);
                  countByProject.set(pid, (countByProject.get(pid) || 0) + 1);
                  titleMatchedProjectIds.add(pid);
                  break;
                }
              }
            }
          }
        }

        let targetProject_ID: number | null = null;
        let targetProjectName: string | null = null;
        let targetExistingBookCount = 0;
        let targetExistingDraftCount = 0;
        let isReassignment = false;

        if (assignedProjectIds.size === 1) {
          const pid = Array.from(assignedProjectIds)[0];
          targetProject_ID = pid;
          const project = projectById.get(pid);
          targetProjectName = (project && typeof project.name === 'string' && project.name) || ('Project #' + pid);
          if (project) {
            targetExistingBookCount = Array.isArray(project.books) ? project.books.length : 0;
            targetExistingDraftCount = Array.isArray(project.drafts) ? project.drafts.length : 0;
          }
        } else if (assignedProjectIds.size > 1) {
          if (!this.allowReassignment) {
            // Ambiguous — legacy behaviour is to skip.
            return;
          }
          // Reassignment mode: pick the Project with the most
          // items in the component as the target. Ties break to
          // the numerically-largest Project ID (i.e. the most
          // recently created) — a reasonable default the operator
          // can override via the primary / remove controls on
          // individual items.
          let bestPid = 0;
          let bestCount = -1;
          countByProject.forEach((n, pid) => {
            if (n > bestCount || (n === bestCount && pid > bestPid)) {
              bestCount = n;
              bestPid = pid;
            }
          });
          targetProject_ID = bestPid;
          const project = projectById.get(bestPid);
          targetProjectName = (project && typeof project.name === 'string' && project.name) || ('Project #' + bestPid);
          if (project) {
            targetExistingBookCount = Array.isArray(project.books) ? project.books.length : 0;
            targetExistingDraftCount = Array.isArray(project.drafts) ? project.drafts.length : 0;
          }
          isReassignment = true;
        }

        // Split the component's items. Anchors are items already
        // assigned to `targetProject_ID`; orphans are unassigned
        // items being proposed for a move. Cross-Project items
        // (only possible in reassignment mode) are assigned to a
        // DIFFERENT existing Project than the target — they are
        // treated like anchors visibility-wise but also drive
        // per-item "Moving from ..." chips.
        const orphanBookKeys: string[] = [];
        const orphanDraftKeys: string[] = [];
        const anchorKeys = new Set<string>();
        const crossProjectMoves = new Map<string, { fromProjectId: number; fromProjectName: string }>();
        const includeAnchors = (targetProject_ID !== null) && (this.suggestExistingProjectChanges || isReassignment);
        for (const k of keys) {
          const it = this.itemsByKey.get(k);
          if (!it) { continue; }
          if (it.assigned && it.workId > 0) {
            if (targetProject_ID !== null && it.workId !== targetProject_ID) {
              // Cross-Project item (reassignment only). Record
              // origin so the template can label the move.
              const originProject = projectById.get(it.workId);
              const originName = (originProject && originProject.name) || ('Project #' + it.workId);
              crossProjectMoves.set(k, { fromProjectId: it.workId, fromProjectName: originName });
              if (it.kind === 'book') { orphanBookKeys.push(k); } else { orphanDraftKeys.push(k); }
              continue;
            }
            // Item already lives on the target Project.
            if (includeAnchors) {
              anchorKeys.add(k);
              if (it.kind === 'book') { orphanBookKeys.push(k); } else { orphanDraftKeys.push(k); }
            }
            // When anchors aren't included they're implicit in
            // the header — nothing to push.
            continue;
          }
          // Truly unassigned item.
          if (it.kind === 'book') { orphanBookKeys.push(k); } else { orphanDraftKeys.push(k); }
        }

        // No orphans → nothing to propose (unless
        // suggestExistingProjectChanges is on and the anchors
        // alone might carry a primary change — checked below).
        const additionsCount = orphanBookKeys.filter(k => !anchorKeys.has(k)).length
                             + orphanDraftKeys.filter(k => !anchorKeys.has(k)).length;
        if (additionsCount === 0 && !this.suggestExistingProjectChanges && !isReassignment) { return; }

        // Previously we skipped lonely orphan singletons in the NEW-PROJECT
        // flavour because the algorithm focused on multi-item groupings.
        // That produced a confusing UX: the banner reported unassigned
        // items while the Organize dialog showed nothing. Allow single-
        // item proposals so users can create a Project for an individual
        // orphan. Only bail when there are no orphans at all.
        if (targetProject_ID === null && (orphanBookKeys.length + orphanDraftKeys.length) < 1) { return; }

        // ── Existing members of the target Project ────────────
        // For add-to-existing / reassignment proposals, surface
        // the Books and Drafts that ALREADY live on the target
        // Project so the operator can see exactly what the
        // Project holds today before agreeing to add to it.
        // Members that are already rendered inside the group's
        // own item list (anchors, surfaced when
        // `suggestExistingProjectChanges` is on, and any item
        // being moved in) are skipped here so nothing is listed
        // twice.
        const existingBookKeys: string[] = [];
        const existingDraftKeys: string[] = [];
        let targetPrimaryBook_ID = 0;
        let targetPrimaryDraft_ID = 0;
        if (targetProject_ID !== null) {
          const targetProject = projectById.get(targetProject_ID);
          targetPrimaryBook_ID = this.projectPrimaryId(targetProject, 'book');
          targetPrimaryDraft_ID = this.projectPrimaryId(targetProject, 'draft');
          const alreadyListed = new Set<string>(orphanBookKeys.concat(orphanDraftKeys));
          const members = this.collectProjectMembers(targetProject_ID, targetProject);
          // Header counts describe the Project's FULL current
          // contents, which is what the operator expects from
          // "already has N books" — so they're taken from the
          // complete member lists, not the filtered display ones.
          targetExistingBookCount = members.bookKeys.length;
          targetExistingDraftCount = members.draftKeys.length;
          for (const k of members.bookKeys) { if (!alreadyListed.has(k)) { existingBookKeys.push(k); } }
          for (const k of members.draftKeys) { if (!alreadyListed.has(k)) { existingDraftKeys.push(k); } }
        }

        // If this component matched an existing Project by title,
        // inject a synthetic reason so the operator sees why the
        // add-to-existing proposal was suggested.
        if (targetProject_ID !== null && titleMatchedProjectIds && titleMatchedProjectIds.has(targetProject_ID)) {
          const reasonMap = rootToReasons.get(root) || new Map<string, number>();
          const label = `Matches Project name "${targetProjectName || ('Project #' + targetProject_ID)}"`;
          // Use a fairly-strong strength so it sorts near the top.
          reasonMap.set(label, Math.max(reasonMap.get(label) || 0, 85));
          rootToReasons.set(root, reasonMap);
        }

        // Order reasons by strength (highest first).
        const reasonSource = rootToReasons.get(root);
        const reasonPairs = reasonSource
          ? Array.from(reasonSource.entries()).sort((x, y) => y[1] - x[1])
          : [];
        const reasons = reasonPairs.map(p => p[0]);
        const strengths = reasonPairs.map(p => p[1]);

        // Original primaries of the target Project — captured so
        // we can highlight changes and issue `setBookAsPrimary` /
        // `setDraftAsPrimary` on save. Only surfaced when either
        // `suggestExistingProjectChanges` or the reassignment
        // flavour is in effect.
        let originalPrimaryBookKey: string | null = null;
        let originalPrimaryDraftKey: string | null = null;
        // Gate on the two options that actually open the existing
        // Project's primaries up to change. Without this gate the
        // keys resolve for ordinary "add to existing" proposals
        // too, which makes `hasPrimaryBookChange()` fire and the
        // save path issue a `setBookAsPrimary` the operator never
        // asked for — the add-to-existing flow is documented as
        // strictly additive. (This became far more likely once
        // existing Project members started being registered in
        // `itemsByKey` for display purposes.)
        if (targetProject_ID !== null && (this.suggestExistingProjectChanges || isReassignment)) {
          const project = projectById.get(targetProject_ID);
          const pbID = this.projectPrimaryId(project, 'book');
          const pdID = this.projectPrimaryId(project, 'draft');
          if (pbID) {
            const key = 'B-' + pbID;
            if (this.itemsByKey.has(key)) { originalPrimaryBookKey = key; }
          }
          if (pdID) {
            const key = 'D-' + pdID;
            if (this.itemsByKey.has(key)) { originalPrimaryDraftKey = key; }
          }
        }

        // Primary suggestion.
        //   - New-Project group: always suggest across all items.
        //   - Add-to-existing (no change): no suggestion — the
        //     existing Project keeps its primaries.
        //   - Add-to-existing (suggestExistingProjectChanges on)
        //     or reassignment: suggest across the full item set
        //     (orphans + anchors + cross-Project moves) so the
        //     operator can re-primary if a better candidate is in
        //     the pool.
        const allowPrimarySuggestion = (targetProject_ID === null)
          || this.suggestExistingProjectChanges
          || isReassignment;
        const suggestBook = allowPrimarySuggestion ? this.suggestPrimary(orphanBookKeys, 'book') : null;
        const suggestDraft = allowPrimarySuggestion ? this.suggestPrimary(orphanDraftKeys, 'draft') : null;

        const g: ProposedGroup = {
          id: 'g' + (groupCounter++),
          bookKeys: orphanBookKeys,
          draftKeys: orphanDraftKeys,
          reasons,
          reasonStrengths: strengths,
          suggestedPrimaryBookKey: suggestBook,
          suggestedPrimaryDraftKey: suggestDraft,
          userPrimaryBookKey: null,
          userPrimaryDraftKey: null,
          accepted: false,
          saving: false,
          saved: false,
          saveError: '',
          manuallyRemovedKeys: new Set<string>(),
          targetProject_ID,
          targetProjectName,
          targetExistingBookCount,
          targetExistingDraftCount,
          existingBookKeys,
          existingDraftKeys,
          existingCollapsed: false,
          targetPrimaryBook_ID,
          targetPrimaryDraft_ID,
          anchorKeys,
          originalPrimaryBookKey,
          originalPrimaryDraftKey,
          crossProjectMoves,
          isReassignment,
          proposedName: '',
          userEditedName: false
        };
        // Compute the initial proposed Project name from the
        // primary(ies) plus any binding suffix — only meaningful
        // for new-Project groups.
        if (targetProject_ID === null) {
          g.proposedName = this.computeProposedName(g);
        }
        // Auto-accept add-to-existing proposals that were matched
        // by Project name so the operator sees them in the reserved
        // section by default (they can still un-accept if undesired).
        if (g.targetProject_ID !== null && titleMatchedProjectIds && titleMatchedProjectIds.has(g.targetProject_ID)) {
          g.accepted = true;
        }

        newGroups.push(g);
      });

      // Backstop: never silently drop an orphan.
      //
      // Several branches above can decline to emit a group. A component
      // whose items span more than one existing Project is skipped as
      // ambiguous unless `allowReassignment` is on; a component with no
      // additions is skipped as having nothing to propose. Each is
      // defensible on its own, but an unfiled item swept into such a
      // component disappeared from the dialog altogether -- while the
      // "Items are not in a Project" banner went on counting it. That is
      // the contradiction operators hit: a banner insisting one Draft
      // needs filing, and a dialog reporting "No proposed groupings
      // found".
      //
      // So once grouping is done, sweep up any unassigned item that did
      // not reach a proposal and offer it on its own as a new Project. A
      // single-item proposal is a weaker suggestion than a matched group,
      // which is why it is the fallback and not the main path -- but it is
      // always actionable, and it holds the invariant the banner depends
      // on: if the banner counts it, the dialog can file it.
      const proposedKeys = new Set<string>();
      for (const g of newGroups) {
        for (const k of g.bookKeys) { if (!g.anchorKeys.has(k)) { proposedKeys.add(k); } }
        for (const k of g.draftKeys) { if (!g.anchorKeys.has(k)) { proposedKeys.add(k); } }
      }
      for (const it of items) {
        if (it.assigned && it.workId > 0) { continue; }
        if (proposedKeys.has(it.key)) { continue; }
        newGroups.push(this.makeSingletonGroup(it, 'g' + (groupCounter++)));
      }

      // Sort groups alphabetically by their suggested / target
      // Project name. For new-Project proposals the sort key is
      // `proposedName` (auto-derived from the primary Book /
      // Draft title, or from the operator's edit); for
      // target-existing proposals the key is `targetProjectName`.
      // Case-insensitive locale compare keeps mixed-case titles
      // grouped naturally. Groups with no name at all sort to
      // the end so they don't disrupt the alphabetical flow.
      newGroups.sort((a, b) => {
        const nameA = (a.targetProjectName || a.proposedName || '').trim();
        const nameB = (b.targetProjectName || b.proposedName || '').trim();
        if (!nameA && nameB) { return 1; }
        if (nameA && !nameB) { return -1; }
        return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
      });

      this.groups = newGroups;
      this.rebuildItemInGroups();
      this.invalidateAttrCache();
      // Successful analyze — capture the option snapshot so the
      // Analyze button can fade out until the operator changes
      // something that would produce a different result.
      this.lastAnalyzeSnapshot = this.optionsSnapshot();
    } catch (e) {
      console.error('OrganizeProjectsComponent.analyze failed', e);
      this.analyzeError = (e && e.message) || 'Analysis failed.';
    } finally {
      this.analyzing = false;
    }
  }

  /**
   * Pick the primary candidate from a list of item keys. Returns null when
   * the list is empty.
   *
   * ╔ The two kinds rank by DIFFERENT fields, deliberately.
   *
   * This mirrors the server's backfill planner exactly, so that the two tools
   * cannot propose different primaries for the same group. That divergence is
   * the specific failure this project has already hit once on the Draft side:
   * a primary chosen by a date the user is not shown is a wrong answer whose
   * reasoning is invisible, which is worse than a wrong answer they can argue
   * with.
   *
   * - **Books rank by `id` DESC.** A Book is an immutable snapshot, so its
   *   `ID` cannot lie about the order the snapshots were taken in — whereas
   *   its `lastUpdate` can: 2,376 of 42,040 live Books carry a `body` or
   *   `cover` row newer than `book.lastUpdate` (server count, 2026-10-24,
   *   `inbox/_processed/2026-10-24b-draft-lastupdate-is-repaired-...`).
   *   Ranking Books by recency mis-picks on exactly those.
   * - **Drafts rank by `lastUpdate` DESC.** A Draft is mutable, so recency is
   *   the real signal. Since the 2026-10-24 data repair the column is always
   *   `>=` its body's and cover's, verified at zero exceptions across all
   *   59,531 Drafts, so it is both the value we display and the accurate one.
   *   Do NOT reintroduce a `GREATEST(draft, body, cover)` rollup here; it is
   *   now arithmetically incapable of returning a different answer.
   *
   * `bestResourceId` breaks a tie on either side. For Books the tie cannot
   * occur (ids are unique), which is the point — no Book primary turns on a
   * timestamp.
   */
  private suggestPrimary(keys: string[], kind: 'book' | 'draft'): string | null {
    let bestKey: string | null = null;
    let bestItem: Extracted | null = null;
    for (const k of keys) {
      const it = this.itemsByKey.get(k);
      if (!it) { continue; }
      if (!bestItem) { bestKey = k; bestItem = it; continue; }
      if (this.comparePrimaryCandidates(it, bestItem, kind) > 0) { bestKey = k; bestItem = it; }
    }
    return bestKey;
  }

  /**
   * Positive when `a` is the better primary candidate than `b`, negative when
   * `b` is, zero when neither can be preferred — in which case
   * {@link suggestPrimary} keeps whichever it saw first, so the result is
   * stable rather than dependent on iteration order.
   */
  private comparePrimaryCandidates(a: Extracted, b: Extracted, kind: 'book' | 'draft'): number {
    if (kind === 'book') {
      if (a.id !== b.id) { return a.id - b.id; }
    } else {
      const aTime = a.lastUpdate.getTime();
      const bTime = b.lastUpdate.getTime();
      if (aTime !== bTime) { return aTime - bTime; }
    }
    if (a.bestResourceId !== b.bestResourceId) { return a.bestResourceId - b.bestResourceId; }
    return 0;
  }

  private rebuildItemInGroups(): void {
    const idx = new Map<string, string[]>();
    for (const g of this.groups) {
      for (const k of g.bookKeys.concat(g.draftKeys)) {
        let list = idx.get(k);
        if (!list) { list = []; idx.set(k, list); }
        list.push(g.id);
      }
    }
    this.itemInGroups = idx;
  }

  // ───────────────────────────────────────────────────────────
  // Template helpers
  // ───────────────────────────────────────────────────────────

  getItem(key: string): Extracted | undefined {
    return this.itemsByKey.get(key);
  }

  /**
   * Resolve the Book that will actually be saved as the group's
   * primary. Order of preference:
   *   1. Operator's explicit override, when it points at a Book
   *      that's still visible in the group (i.e. not manually
   *      removed).
   *   2. The originally-suggested primary Book, when it's still
   *      visible.
   *   3. Any first visible Book — enforces the invariant that a
   *      Project with Books MUST have exactly one primary Book, so
   *      when the operator has whittled the visible set down to a
   *      single Book that Book is automatically the primary
   *      (whether or not the operator explicitly starred it).
   * Returns null only when NO Book is visible in the group — in
   * which case there's no primary to save (and no Books to move).
   *
   * The equivalent rule applies to Drafts via
   * `getEffectivePrimaryDraftKey()`. Together these ensure only
   * one Book and only one Draft is ever persisted as primary,
   * regardless of how the operator navigated to the selection.
   */
  getEffectivePrimaryBookKey(group: ProposedGroup): string | null {
    return this.pickEffectivePrimary(group, 'book');
  }

  getEffectivePrimaryDraftKey(group: ProposedGroup): string | null {
    return this.pickEffectivePrimary(group, 'draft');
  }

  private pickEffectivePrimary(group: ProposedGroup, kind: 'book' | 'draft'): string | null {
    const keys = kind === 'book' ? group.bookKeys : group.draftKeys;
    const userKey = kind === 'book' ? group.userPrimaryBookKey : group.userPrimaryDraftKey;
    const suggested = kind === 'book' ? group.suggestedPrimaryBookKey : group.suggestedPrimaryDraftKey;
    const isVisible = (k: string | null): boolean =>
      !!k && keys.indexOf(k) >= 0 && !group.manuallyRemovedKeys.has(k);
    if (isVisible(userKey)) { return userKey; }
    if (isVisible(suggested)) { return suggested; }
    // Fall back to the first still-visible item so a single
    // remaining Book / Draft is always the primary.
    for (const k of keys) { if (isVisible(k)) { return k; } }
    return null;
  }

  /**
   * True when `key` is what the analysis originally proposed as
   * primary — regardless of whether the operator has since
   * overridden the choice. Used to render the "star_half" hint on
   * the originally-suggested item and the "Originally suggested"
   * secondary note underneath it.
   */
  isSuggestedPrimary(group: ProposedGroup, key: string): boolean {
    return group.suggestedPrimaryBookKey === key || group.suggestedPrimaryDraftKey === key;
  }

  /**
   * True when the operator has explicitly overridden the
   * suggested primary for the same kind (Book / Draft) as `key`.
   * Drives the "the user's pick wins" secondary note next to the
   * originally-suggested item once it's no longer the effective
   * primary.
   */
  isOverriddenSuggestion(group: ProposedGroup, key: string): boolean {
    if (!this.isSuggestedPrimary(group, key)) { return false; }
    if (this.isEffectivePrimary(group, key)) { return false; }
    const it = this.itemsByKey.get(key);
    if (!it) { return false; }
    if (it.kind === 'book') {
      return !!group.userPrimaryBookKey && group.userPrimaryBookKey !== key;
    }
    return !!group.userPrimaryDraftKey && group.userPrimaryDraftKey !== key;
  }

  isEffectivePrimary(group: ProposedGroup, key: string): boolean {
    return this.getEffectivePrimaryBookKey(group) === key
        || this.getEffectivePrimaryDraftKey(group) === key;
  }

  /**
   * Determine whether an item's card should be hidden inside a
   * given group. It hides in two cases:
   * 1. The operator manually removed it from the group.
   * 2. Some OTHER accepted group also lists it — accepted groups
   *    reserve their items and hide them from all others until
   *    the operator un-accepts.
   */
  isItemHiddenInGroup(group: ProposedGroup, key: string): boolean {
    if (group.manuallyRemovedKeys.has(key)) { return true; }
    if (group.accepted) { return false; }
    const groupsForItem = this.itemInGroups.get(key) || [];
    for (const otherId of groupsForItem) {
      if (otherId === group.id) { continue; }
      const other = this.groups.find(g => g.id === otherId);
      if (other && other.accepted && !other.manuallyRemovedKeys.has(key)) { return true; }
    }
    return false;
  }

  /**
   * True when the item appears in more than one non-hidden group
   * (from THIS group's perspective) — surfaces a "also in group X"
   * badge next to the item.
   */
  otherGroupsForItem(group: ProposedGroup, key: string): string[] {
    const all = this.itemInGroups.get(key) || [];
    return all.filter(id => id !== group.id);
  }

  /**
   * Number of items in a group that are currently visible (not
   * manually removed and not shadowed by an accepted sibling
   * group) — used to disable Save on effectively-empty groups.
   */
  visibleItemCount(group: ProposedGroup): number {
    let n = 0;
    for (const k of group.bookKeys.concat(group.draftKeys)) {
      if (!this.isItemHiddenInGroup(group, k)) { n++; }
    }
    return n;
  }

  visibleBookCount(group: ProposedGroup): number {
    let n = 0;
    for (const k of group.bookKeys) { if (!this.isItemHiddenInGroup(group, k)) { n++; } }
    return n;
  }

  visibleDraftCount(group: ProposedGroup): number {
    let n = 0;
    for (const k of group.draftKeys) { if (!this.isItemHiddenInGroup(group, k)) { n++; } }
    return n;
  }

  // ───────────────────────────────────────────────────────────
  // Attribute analysis — "differences" strip + chip coloring
  // ───────────────────────────────────────────────────────────

  /** Discard any cached attribute analysis; call after any mutation that changes group visibility. */
  private invalidateAttrCache(): void {
    this.attrCache.clear();
  }

  /**
   * Return (and cache) the per-attribute tally for a group across
   * its currently VISIBLE items. Visibility here follows the same
   * rules as `isItemHiddenInGroup()` — manually removed items and
   * items shadowed by other accepted groups are excluded, so the
   * differences the operator sees always match the item cards
   * displayed underneath.
   */
  getAttrAnalysis(group: ProposedGroup): GroupAttrAnalysis {
    const cached = this.attrCache.get(group.id);
    if (cached) { return cached; }
    const items: Extracted[] = [];
    for (const k of group.bookKeys.concat(group.draftKeys)) {
      if (this.isItemHiddenInGroup(group, k)) { continue; }
      const it = this.itemsByKey.get(k);
      if (it) { items.push(it); }
    }
    const tally = (getter: (it: Extracted) => any, isEmpty: (v: any) => boolean): AttrTally => {
      const counts = new Map<string, { v: any; n: number }>();
      let hasValue = false;
      let sawEmpty = false;
      for (const it of items) {
        const v = getter(it);
        if (isEmpty(v)) { sawEmpty = true; continue; }
        hasValue = true;
        const k = String(v);
        const cur = counts.get(k);
        if (cur) { cur.n++; } else { counts.set(k, { v, n: 1 }); }
      }
      let mode: any = null;
      let modeCount = 0;
      counts.forEach(e => { if (e.n > modeCount) { modeCount = e.n; mode = e.v; } });
      // Unanimous only when every item had a value AND all agreed.
      const unanimous = hasValue && !sawEmpty && counts.size === 1;
      return { mode, unanimous, hasValue };
    };

    const cover = tally(it => it.coverResourceId, v => !v);
    const body = tally(it => it.bodyResourceId, v => !v);
    const trim = tally(
      it => (it.width && it.height) ? `${Math.round(it.width)}x${Math.round(it.height)}` : '',
      v => !v
    );
    const binding = tally(it => it.bindingType || '', v => !v);
    const paper = tally(it => it.paperTypeId, v => !v);
    const laminate = tally(it => it.laminateType || '', v => !v);
    const title = tally(it => (it.title || '').toLowerCase(), v => !v);

    const differences: string[] = [];
    // Order roughly matches `scorePair` weights so the most
    // meaningful differences bubble to the front.
    if (items.length > 1) {
      if (cover.hasValue && !cover.unanimous) { differences.push('Different cover resources'); }
      if (body.hasValue && !body.unanimous) { differences.push('Different body resources'); }
      if (title.hasValue && !title.unanimous) { differences.push('Different titles'); }
      if (trim.hasValue && !trim.unanimous) { differences.push('Different trim sizes'); }
      if (binding.hasValue && !binding.unanimous) { differences.push('Different binding'); }
      if (paper.hasValue && !paper.unanimous) { differences.push('Different paper types'); }
      if (laminate.hasValue && !laminate.unanimous) { differences.push('Different lamination'); }
    }

    const analysis: GroupAttrAnalysis = { cover, body, trim, binding, paper, laminate, title, differences };
    this.attrCache.set(group.id, analysis);
    return analysis;
  }

  /**
   * Classify an item's value for `attr` relative to the group's
   * visible-items mode: `'same'` when it matches the mode (cool /
   * light chip), `'different'` when it disagrees (warm chip), and
   * `''` when there's nothing to compare (single-item group, item
   * lacks the attribute, or the whole group lacks the attribute).
   * The template consumes this via `[ngClass]` to color chips.
   */
  attrMatchClass(group: ProposedGroup, key: string, attr: AttrKey): '' | 'same' | 'different' {
    const it = this.itemsByKey.get(key);
    if (!it) { return ''; }
    const a = this.getAttrAnalysis(group);
    const info = a[attr];
    if (!info || !info.hasValue) { return ''; }
    // Extract the item's own value for this attribute in the same
    // shape used by the tally so string comparison is exact.
    let val: any;
    switch (attr) {
      case 'cover': val = it.coverResourceId; break;
      case 'body': val = it.bodyResourceId; break;
      case 'trim': val = (it.width && it.height) ? `${Math.round(it.width)}x${Math.round(it.height)}` : ''; break;
      case 'binding': val = it.bindingType || ''; break;
      case 'paper': val = it.paperTypeId; break;
      case 'laminate': val = it.laminateType || ''; break;
      case 'title': val = (it.title || '').toLowerCase(); break;
    }
    const empty = (attr === 'cover' || attr === 'body' || attr === 'paper') ? !val : val === '';
    if (empty) { return ''; }
    // With only one visible item there's no meaningful compare.
    if (info.unanimous) { return 'same'; }
    return String(val) === String(info.mode) ? 'same' : 'different';
  }

  /** Rough strength for a difference label — used to pick chip emphasis. */
  differenceStrength(label: string): number {
    if (label.indexOf('cover resource') >= 0 || label.indexOf('body resource') >= 0) { return 90; }
    if (label.indexOf('title') >= 0) { return 60; }
    return 30;
  }

  /**
   * Save gating.
   *
   * - For NEW-Project groups (`targetProject_ID === null`): if the
   *   group has Books, a primary Book must be resolvable; same for
   *   Drafts. This matches the invariant `POST /project/createproject`
   *   enforces server-side.
   * - For ADD-TO-EXISTING groups (`targetProject_ID > 0`): the
   *   existing Project already carries its own primaries. We just
   *   need at least one visible orphan to move; no primary
   *   selection is required.
   */
  canSaveGroup(group: ProposedGroup): boolean {
    if (group.saving || group.saved) { return false; }
    if (this.visibleItemCount(group) < 1) { return false; }
    if (group.targetProject_ID != null) {
      // Add-to-existing: any visible orphan qualifies.
      return true;
    }
    const hasBooks = this.visibleBookCount(group) > 0;
    const hasDrafts = this.visibleDraftCount(group) > 0;
    if (hasBooks) {
      const key = this.getEffectivePrimaryBookKey(group);
      if (!key || group.manuallyRemovedKeys.has(key)) { return false; }
    }
    if (hasDrafts) {
      const key = this.getEffectivePrimaryDraftKey(group);
      if (!key || group.manuallyRemovedKeys.has(key)) { return false; }
    }
    // A Project name is mandatory server-side. The field is
    // auto-suggested but the operator can clear it — refuse to
    // save until it's been repopulated.
    if (!(group.proposedName || '').trim()) { return false; }
    return hasBooks || hasDrafts;
  }

  // ───────────────────────────────────────────────────────────
  // Operator actions
  // ───────────────────────────────────────────────────────────

  toggleAccepted(group: ProposedGroup): void {
    group.accepted = !group.accepted;
    // Cross-group shadowing changes visibility everywhere.
    this.invalidateAttrCache();
  }

  removeFromGroup(group: ProposedGroup, key: string): void {
    group.manuallyRemovedKeys.add(key);
    // If the removed item was the primary, clear any user override
    // so the next visible item is re-suggested downstream.
    if (group.userPrimaryBookKey === key) { group.userPrimaryBookKey = null; }
    if (group.userPrimaryDraftKey === key) { group.userPrimaryDraftKey = null; }
    this.invalidateAttrCache();
    this.refreshProposedName(group);
  }

  restoreInGroup(group: ProposedGroup, key: string): void {
    group.manuallyRemovedKeys.delete(key);
    this.invalidateAttrCache();
    this.refreshProposedName(group);
  }

  /**
   * Peel `key` off `group` and place it in a BRAND-NEW proposed
   * Project (`targetProject_ID = null`) inserted immediately
   * after `group` in `this.groups`. The peeled item becomes the
   * sole member of the new group, so it is trivially its own
   * primary and the new group's suggested name is derived from
   * the item's title via the existing `computeProposedName()`
   * path.
   *
   * Guardrails:
   *  - No-op when `group.saved` — a saved group's items are
   *    already persisted; splitting them here would be lying to
   *    the operator.
   *  - No-op when the item isn't currently in `group`'s
   *    `bookKeys` / `draftKeys` (defensive; template only
   *    exposes the button on visible items).
   *  - The item is REMOVED from `group` (not just hidden) so it
   *    stops participating in `group`'s auto-suggested primary
   *    / name / attribute analysis. That way both groups look
   *    independent from the moment of the split.
   *  - Anchor / cross-project items — items only in the group
   *    because they live on the target Project — can also be
   *    split, but splitting them means their currentProjectId
   *    still applies and the new-Project save flow will move
   *    them off their existing Project. That's the same outcome
   *    the operator would get from clicking Save on the current
   *    group after removing the item, so the semantics are
   *    consistent.
   */
  splitToNewGroup(group: ProposedGroup, key: string): void {
    if (!group || group.saved) { return; }
    const it = this.itemsByKey.get(key);
    if (!it) { return; }
    const isBook = it.kind === 'book';
    const sourceList = isBook ? group.bookKeys : group.draftKeys;
    const idxInSource = sourceList.indexOf(key);
    if (idxInSource < 0) { return; }

    // Detach the item from the source group so the split feels
    // like a real move rather than a duplication.
    sourceList.splice(idxInSource, 1);
    group.manuallyRemovedKeys.delete(key); // no longer in this group at all
    if (group.userPrimaryBookKey === key) { group.userPrimaryBookKey = null; }
    if (group.userPrimaryDraftKey === key) { group.userPrimaryDraftKey = null; }
    if (group.suggestedPrimaryBookKey === key) { group.suggestedPrimaryBookKey = null; }
    if (group.suggestedPrimaryDraftKey === key) { group.suggestedPrimaryDraftKey = null; }
    // If the source group is a target-existing / reassignment
    // flavour, the anchor / cross-project bookkeeping needs to
    // drop the moved key too — the new group is a plain
    // new-Project proposal with no anchors.
    if (group.anchorKeys) { group.anchorKeys.delete(key); }
    if (group.crossProjectMoves) { group.crossProjectMoves.delete(key); }

    // Fresh single-item new-Project group. Suggested primary is
    // trivially the item itself; user primary starts unset so
    // `pickEffectivePrimary()` falls back to the suggested key.
    const newGroup: ProposedGroup = {
      id: 'g-split-' + Date.now() + '-' + Math.floor(Math.random() * 10000),
      bookKeys: isBook ? [key] : [],
      draftKeys: isBook ? [] : [key],
      reasons: [],
      reasonStrengths: [],
      suggestedPrimaryBookKey: isBook ? key : null,
      suggestedPrimaryDraftKey: isBook ? null : key,
      userPrimaryBookKey: null,
      userPrimaryDraftKey: null,
      accepted: false,
      saving: false,
      saved: false,
      saveError: '',
      manuallyRemovedKeys: new Set<string>(),
      targetProject_ID: null,
      targetProjectName: null,
      targetExistingBookCount: 0,
      targetExistingDraftCount: 0,
      existingBookKeys: [],
      existingDraftKeys: [],
      existingCollapsed: false,
      targetPrimaryBook_ID: 0,
      targetPrimaryDraft_ID: 0,
      anchorKeys: new Set<string>(),
      originalPrimaryBookKey: null,
      originalPrimaryDraftKey: null,
      crossProjectMoves: new Map<string, { fromProjectId: number; fromProjectName: string }>(),
      isReassignment: false,
      proposedName: '',
      userEditedName: false
    };
    newGroup.proposedName = this.computeProposedName(newGroup);

    // Insert immediately after the source group so it lands
    // adjacent in the rendered list, satisfying the "shows
    // adjacent to the current proposed Project" requirement.
    const srcIdx = this.groups.indexOf(group);
    if (srcIdx >= 0) {
      this.groups.splice(srcIdx + 1, 0, newGroup);
    } else {
      this.groups.push(newGroup);
    }

    // Source group's derived state needs a refresh — the item
    // list changed, so the auto-suggested name and attr tally
    // both need to be re-derived.
    this.refreshProposedName(group);
    this.rebuildItemInGroups();
    this.invalidateAttrCache();
  }

  setPrimary(group: ProposedGroup, key: string): void {
    const it = this.itemsByKey.get(key);
    if (!it) { return; }
    if (it.kind === 'book') {
      group.userPrimaryBookKey = key;
    } else {
      group.userPrimaryDraftKey = key;
    }
    this.refreshProposedName(group);
  }

  clearPrimaryOverride(group: ProposedGroup, kind: 'book' | 'draft'): void {
    if (kind === 'book') { group.userPrimaryBookKey = null; } else { group.userPrimaryDraftKey = null; }
    this.refreshProposedName(group);
  }

  /**
   * `[(ngModel)]`-style handler for the per-group Project name
   * text input. Flags the group so `refreshProposedName()` stops
   * overwriting the operator's edits on subsequent primary /
   * removal changes. An empty (or whitespace-only) value resets
   * the flag so the auto-suggestion resumes.
   */
  onProposedNameInput(group: ProposedGroup, value: string): void {
    group.proposedName = value;
    const trimmed = (value || '').trim();
    if (!trimmed) {
      // Empty field → operator wants the suggestion back.
      group.userEditedName = false;
      group.proposedName = this.computeProposedName(group);
    } else {
      group.userEditedName = true;
    }
  }

  /**
   * Recompute the suggested Project name from the current primary
   * Book / Draft title and, if `requireSameBinding` is on, the
   * group's binding type. Skips when the operator has typed a
   * custom name — their edit wins until they blank the field.
   * No-op for add-to-existing groups (they inherit the existing
   * Project's name).
   */
  private refreshProposedName(group: ProposedGroup): void {
    if (group.targetProject_ID != null) { return; }
    if (group.userEditedName) { return; }
    group.proposedName = this.computeProposedName(group);
  }

  /**
   * Build the auto-suggested Project name for a group. Format is
   * `<primary title>` — or `<primary title> — <binding>` when the
   * "require same binding" option is engaged, so parallel
   * hardcover / softcover editions of the same title propose two
   * separately-named Projects instead of collapsing into one.
   */
  private computeProposedName(group: ProposedGroup): string {
    const pbKey = this.getEffectivePrimaryBookKey(group);
    const pdKey = this.getEffectivePrimaryDraftKey(group);
    const pb = pbKey ? this.itemsByKey.get(pbKey) : null;
    const pd = pdKey ? this.itemsByKey.get(pdKey) : null;
    let base = (pb && pb.title) || (pd && pd.title) || '';
    if (this.requireSameBinding) {
      const binding = this.pickGroupBinding(group);
      if (binding) {
        base = base ? `${base} — ${binding}` : binding;
      }
    }
    return base;
  }

  /**
   * Return a representative binding type for the group by scanning
   * its visible items. When `requireSameBinding` is on every
   * visible item shares the same binding, so the first non-empty
   * hit is correct.
   */
  private pickGroupBinding(group: ProposedGroup): string {
    for (const k of group.bookKeys.concat(group.draftKeys)) {
      if (this.isItemHiddenInGroup(group, k)) { continue; }
      const it = this.itemsByKey.get(k);
      if (it && it.bindingType) { return it.bindingType; }
    }
    return '';
  }

  // ───────────────────────────────────────────────────────────
  // Save
  // ───────────────────────────────────────────────────────────

  /**
   * Persist a proposed group.
   *
   * - When `targetProject_ID` is null → create a new Project via
   *   the consolidated `POST /project/createproject` endpoint. One
   *   request handles create + moves + primary assignment; partial
   *   failures come back in `data.warnings[]` and are surfaced to
   *   the operator.
   * - When `targetProject_ID` is set → add the group's orphan
   *   items into that existing Project via
   *   `moveBookToProject` / `moveDraftToProject`. Each move fires
   *   in parallel and we forkJoin the responses; any per-item
   *   failure is summarised into `group.saveError`. Primary
   *   assignment is deliberately left untouched — the existing
   *   Project already carries its primaries.
   *
   * See inbox note
   * `2026-08-04-consolidated-createproject-endpoint.md` for the
   * new-Project endpoint's contract.
   */
  saveGroup(group: ProposedGroup): void {
    if (!this.canSaveGroup(group)) { return; }

    if (group.targetProject_ID != null && group.targetProject_ID > 0) {
      this.saveGroupIntoExistingProject(group, group.targetProject_ID);
      return;
    }

    // ── New-Project flow (unchanged from prior behaviour) ──
    const primaryBookKey = this.getEffectivePrimaryBookKey(group);
    const primaryDraftKey = this.getEffectivePrimaryDraftKey(group);
    const primaryBook = primaryBookKey ? this.itemsByKey.get(primaryBookKey) : null;
    const primaryDraft = primaryDraftKey ? this.itemsByKey.get(primaryDraftKey) : null;

    // Prefer the operator-editable name field. Falls back to a
    // fresh auto-suggestion if the field was left blank (which
    // `onProposedNameInput` already refills, but be defensive).
    let projectName = (group.proposedName || '').trim();
    if (!projectName) {
      projectName = this.computeProposedName(group).trim();
    }
    if (!projectName) {
      group.saveError = 'Project name is empty — set a primary with a title or type a name.';
      return;
    }

    // Guard against another Project already having that name.
    for (const p of (this.projectService.objects || [])) {
      if (p && p.name === projectName) {
        // If an existing Project already has this name, it's
        // possible the analysis suggested a new Project with the
        // same title as an existing Project (e.g. same primary
        // Book title). In that case prefer to add the group's
        // items into the existing Project rather than failing —
        // this matches operator intent and avoids confusing
        // "name already exists" errors after the analysis.
        console.warn(`OrganizeProjects: project name conflict for \"${projectName}\". Falling back to add-to-existing Project ID ${p.ID}.`);
        this.saveGroupIntoExistingProject(group, p.ID);
        return;
      }
    }

    group.saving = true;
    group.saveError = '';

    // Collect the items we'll move — snapshot current visibility.
    const book_IDs: number[] = [];
    const draft_IDs: number[] = [];
    for (const k of group.bookKeys) {
      if (this.isItemHiddenInGroup(group, k)) { continue; }
      const it = this.itemsByKey.get(k);
      if (it) { book_IDs.push(it.id); }
    }
    for (const k of group.draftKeys) {
      if (this.isItemHiddenInGroup(group, k)) { continue; }
      const it = this.itemsByKey.get(k);
      if (it) { draft_IDs.push(it.id); }
    }

    // No special-case: use the consolidated `createProject` endpoint
    // for all new-Project saves. The server's consolidated endpoint
    // performs create + moves + primary assignment and returns a
    // structured `data.warnings[]` array on partial success; prefer
    // this single-call contract to avoid client/server races.

    // Fallback to the consolidated createProject path for all other
    // multi-item or mixed Book/Draft groups. The consolidated call
    // reports per-item warnings in `data.warnings[]` which we map
    // into a concise operator-facing summary.
    // Defensive: ensure arrays are deduplicated and that the primary
    // IDs are present in the payload. Log the request so server-side
    // diagnostics can be correlated with the client's intent.
    const dedupe = (arr: number[]) => Array.from(new Set(arr || []));
    const outBookIDs = dedupe(book_IDs || []);
    const outDraftIDs = dedupe(draft_IDs || []);
    if (primaryBook && outBookIDs.indexOf(primaryBook.id) < 0) { outBookIDs.push(primaryBook.id); }
    if (primaryDraft && outDraftIDs.indexOf(primaryDraft.id) < 0) { outDraftIDs.push(primaryDraft.id); }

    const req: CreateProjectRequest = {
      name: projectName,
      folder: this.selectedFolder || '/',
      primaryBook_ID: primaryBook ? primaryBook.id : 0,
      primaryDraft_ID: primaryDraft ? primaryDraft.id : 0,
      book_IDs: outBookIDs,
      draft_IDs: outDraftIDs,
      // Never seed an empty Draft — we're bringing in existing content.
      seedEmptyDraft: false
    };

    // Emit a debug trace so the operator / dev can inspect the exact
    // payload that was sent when troubleshooting move/primary failures.
    // This is intentionally a debug-level log so it can be left in
    // afterwards; the server message will still be the authoritative
    // source of truth for partial failures.
    console.debug('OrganizeProjects: createProject request', req);

    this.projectService.createProject(req).subscribe(
      envelope => {
        group.saving = false;
        if (!envelope) {
          group.saveError = 'Create Project failed. See console.';
          return;
        }
        if (envelope.status !== 'SUCCESS' || !envelope.data) {
          group.saveError = envelope.message || 'Create Project failed.';
          console.error('createProject returned non-SUCCESS envelope', envelope);
          return;
        }
        const warnings = envelope.data.warnings || [];
        if (warnings.length > 0) {
          group.saveError = this.summarizeCreateProjectWarnings(warnings);
          console.warn('createProject completed with warnings', envelope.data);
          // Still mark the group saved — the Project exists; the
          // operator can see which items didn't make it in and act
          // on them separately. Do NOT flip `accepted` — leave the
          // affected items visible so they can be retried.
          group.saved = true;
          return;
        }
        group.saved = true;
        group.accepted = true; // implicitly reserves the items.
      },
      err => {
        group.saving = false;
        // If server responded with a 409 / name-collision, attempt
        // to fall back to the existing add-to-existing flow. This
        // handles races where the Project was created server-side
        // after our local cache was built.
        if (err && typeof err.status === 'number' && err.status === 409) {
          console.warn(`OrganizeProjects: createProject returned 409 for "${projectName}"; attempting add-to-existing fallback.`);
          const existing = (this.projectService.objects || []).find(p => p && p.name === projectName && typeof p.ID === 'number');
          if (existing && existing.ID) {
            this.saveGroupIntoExistingProject(group, existing.ID);
            return;
          }
          group.saveError = 'Project name conflict: server reports the name already exists. Please retry or choose a different name.';
          console.warn('createProject 409 but existing Project not found in client cache', err);
          return;
        }

        group.saveError = 'Create Project failed. See console.';
        console.error('createProject failed', err);
      }
    );
  }

  /**
   * Move each visible orphan Book / Draft in `group` onto the
   * existing Project identified by `targetProjectId` using the
   * per-item `moveBookToProject` / `moveDraftToProject`
   * endpoints. All moves fire in parallel; per-item failures are
   * captured and rolled up into `group.saveError`. The group is
   * marked `saved` once every move has settled — success or fail
   * — so the operator can retry any laggards manually via the
   * ordinary Projects UI.
   *
   * The consolidated `POST /project/createproject` endpoint (see
   * `2026-08-04-consolidated-createproject-endpoint.md`) doesn't
   * accept an existing Project ID target — it always creates a
   * new one — so the "add to existing" flow stays on the per-item
   * move endpoints. Those endpoints were hardened server-side on
   * 2026-08-04 to actually persist `work_ID` and to accept
   * `currentProjectId === 0` for previously-unassigned items, so
   * the pattern below is safe.
   */
  private saveGroupIntoExistingProject(group: ProposedGroup, targetProjectId: number): void {
    group.saving = true;
    group.saveError = '';

    interface MoveResult {
      kind: 'book' | 'draft' | 'primaryBook' | 'primaryDraft';
      id: number;
      ok: boolean;
      message?: string;
    }

    const moveObservables = [];
    for (const k of group.bookKeys) {
      if (this.isItemHiddenInGroup(group, k)) { continue; }
      const it = this.itemsByKey.get(k);
      if (!it) { continue; }
      const bookId = it.id;
      const currentProjectId = it.workId || 0;
      // Skip items already on the target Project — they're
      // anchors surfaced only so the operator could see them
      // (and possibly re-primary them). A move-in-place call
      // would be wasteful and could bump lastUpdate timestamps
      // for no reason.
      if (currentProjectId === targetProjectId) { continue; }
      moveObservables.push(
        this.projectService.moveBookToProject(bookId, currentProjectId, targetProjectId).pipe(
          map(response => this.interpretMoveResponse('book', bookId, response)),
          catchError(err => {
            console.error(`moveBookToProject bookId=${bookId} failed`, err);
            return of<MoveResult>({ kind: 'book', id: bookId, ok: false, message: this.errMsg(err) });
          })
        )
      );
    }
    for (const k of group.draftKeys) {
      if (this.isItemHiddenInGroup(group, k)) { continue; }
      const it = this.itemsByKey.get(k);
      if (!it) { continue; }
      const draftId = it.id;
      const currentProjectId = it.workId || 0;
      if (currentProjectId === targetProjectId) { continue; }
      moveObservables.push(
        this.projectService.moveDraftToProject(draftId, currentProjectId, targetProjectId).pipe(
          map(response => this.interpretMoveResponse('draft', draftId, response)),
          catchError(err => {
            console.error(`moveDraftToProject draftId=${draftId} failed`, err);
            return of<MoveResult>({ kind: 'draft', id: draftId, ok: false, message: this.errMsg(err) });
          })
        )
      );
    }

    // Capture any desired primary changes but DO NOT schedule
    // them into the same forkJoin as the per-item moves. Calling
    // `set*AsPrimary` before the moves have settled on the server
    // can race and produce `primary*Skipped` warnings. Instead,
    // we'll execute the moves first and then invoke primary-change
    // calls only after the moves succeed (or at least have
    // completed) so the server-side invariant is satisfied.
    let desiredPrimaryBookId: number | null = null;
    let desiredPrimaryDraftId: number | null = null;
    if (this.hasPrimaryBookChange(group)) {
      const effKey = this.getEffectivePrimaryBookKey(group);
      const effIt = effKey ? this.itemsByKey.get(effKey) : null;
      if (effIt) { desiredPrimaryBookId = effIt.id; }
    }
    if (this.hasPrimaryDraftChange(group)) {
      const effKey = this.getEffectivePrimaryDraftKey(group);
      const effIt = effKey ? this.itemsByKey.get(effKey) : null;
      if (effIt) { desiredPrimaryDraftId = effIt.id; }
    }

    if (moveObservables.length === 0) {
      group.saving = false;
      group.saveError = 'No changes to apply.';
      return;
    }

    forkJoin(moveObservables).subscribe(
      (results: MoveResult[]) => {
        // Moves finished (some may have failed). We'll now
        // separately attempt primary assignments when applicable.
        const moveFailures = results.filter(r => !r.ok && (r.kind === 'book' || r.kind === 'draft'));
        if (moveFailures.length > 0) {
          const parts = moveFailures.map(f => `${f.kind}#${f.id}${f.message ? ' — ' + f.message : ''}`);
          group.saveError = `Some items could not be moved to Project #${targetProjectId}: ${parts.join('; ')}`;
          console.warn('saveGroupIntoExistingProject move partial failure', results);
          // Mark saved so the operator can retry failed items; do
          // not mark accepted so failures remain visible.
          group.saving = false;
          group.saved = true;
          return;
        }

        // All moves succeeded; proceed to primary assignments if
        // requested. Execute primary assignments sequentially so
        // we can present precise diagnostics.
        const primaryResults: MoveResult[] = [];
        const doPrimaryBook = (): Promise<void> => {
          return new Promise(resolve => {
            if (desiredPrimaryBookId == null) { resolve(); return; }
            this.projectService.setBookAsPrimary(targetProjectId, desiredPrimaryBookId).pipe(
              map(response => this.interpretMoveResponse('primaryBook', desiredPrimaryBookId as number, response)),
              catchError(err => {
                console.error(`setBookAsPrimary bookId=${desiredPrimaryBookId} failed`, err);
                return of<MoveResult>({ kind: 'primaryBook', id: desiredPrimaryBookId as number, ok: false, message: this.errMsg(err) });
              })
            ).subscribe(r => { primaryResults.push(r); resolve(); });
          });
        };
        const doPrimaryDraft = (): Promise<void> => {
          return new Promise(resolve => {
            if (desiredPrimaryDraftId == null) { resolve(); return; }
            this.projectService.setDraftAsPrimary(targetProjectId, desiredPrimaryDraftId).pipe(
              map(response => this.interpretMoveResponse('primaryDraft', desiredPrimaryDraftId as number, response)),
              catchError(err => {
                console.error(`setDraftAsPrimary draftId=${desiredPrimaryDraftId} failed`, err);
                return of<MoveResult>({ kind: 'primaryDraft', id: desiredPrimaryDraftId as number, ok: false, message: this.errMsg(err) });
              })
            ).subscribe(r => { primaryResults.push(r); resolve(); });
          });
        };

        // Run primary assignments in sequence.
        doPrimaryBook().then(() => doPrimaryDraft()).then(() => {
          group.saving = false;
          const primaryFailures = primaryResults.filter(r => !r.ok);
          if (primaryFailures.length === 0) {
            group.saved = true;
            group.accepted = true;
            return;
          }
          // Primary assignment(s) failed — surface friendly summary
          // but leave the group saved so the operator can retry.
          const parts = primaryFailures.map(f => `${f.kind}#${f.id}${f.message ? ' — ' + f.message : ''}`);
          group.saveError = `Project updated but primary assignment(s) failed for Project #${targetProjectId}: ${parts.join('; ')}`;
          console.warn('saveGroupIntoExistingProject primary assignment failure', primaryResults);
          group.saved = true;
        }).catch(err => {
          group.saving = false;
          group.saveError = 'Save-to-Project failed during primary assignment. See console.';
          console.error('saveGroupIntoExistingProject primary assignment chain failed', err);
        });
      },
      err => {
        group.saving = false;
        group.saveError = 'Save-to-Project failed. See console.';
        console.error('saveGroupIntoExistingProject forkJoin failed', err);
      }
    );
  }

  /**
   * Interpret the (untyped) response envelope that
   * `moveBookToProject` / `moveDraftToProject` emit. The server
   * returns either the ResponseMessage envelope
   * `{status:'SUCCESS'|'ERROR', message, data}` or (rarely, older
   * paths) a bare truthy value. We treat "not an envelope" as
   * success on the theory that the observable emitted something
   * without throwing.
   */
  private interpretMoveResponse(kind: 'book' | 'draft' | 'primaryBook' | 'primaryDraft', id: number, response: any): { kind: 'book' | 'draft' | 'primaryBook' | 'primaryDraft'; id: number; ok: boolean; message?: string } {
    if (response == null) {
      // `catchError` fallback would already handle a real error;
      // a null / undefined here means the ProjectService's shared
      // `handleErrorX` swallowed a failure into a default. Treat
      // as a failure so the operator sees something is wrong.
      return { kind, id, ok: false, message: 'No response from server' };
    }
    if (typeof response === 'object' && 'status' in response) {
      if (response.status === 'SUCCESS') {
        return { kind, id, ok: true };
      }
      return { kind, id, ok: false, message: response.message || `Server returned ${response.status}` };
    }
    // Bare truthy response — treat as success.
    return { kind, id, ok: true };
  }

  private errMsg(err: any): string {
    if (!err) { return ''; }
    if (typeof err === 'string') { return err; }
    if (typeof err.status === 'number' && err.status > 0) {
      return `HTTP ${err.status}${err.statusText ? ' ' + err.statusText : ''}`;
    }
    return err.message || 'Network error';
  }

  /**
   * Build a short operator-facing summary from the server's
   * per-item warnings taxonomy. Keeps the dialog small; the full
   * payload is already in the browser console.
   */
  private summarizeCreateProjectWarnings(warnings: CreateProjectWarning[]): string {
    // Map known server warning kinds to friendlier, operator-facing labels.
    const kindLabels: { [k: string]: string } = {
      moveBook: 'book(s) not moved',
      moveDraft: 'draft(s) not moved',
      primaryBookSkipped: 'primary book not set',
      primaryDraftSkipped: 'primary draft not set',
      primaryBookInvariant: 'primary book invariant',
      primaryDraftInvariant: 'primary draft invariant',
      seedDraft: 'seeded draft',
      seedDraftInvariant: 'seed draft invariant'
    };

    const counts: { [k: string]: number } = {};
    const details: { [k: string]: string[] } = {};
    for (const w of warnings) {
      const k = w && w.kind ? w.kind : 'unknown';
      counts[k] = (counts[k] || 0) + 1;
      // Capture any server-provided message for context.
      if (w && w.message) {
        (details[k] = details[k] || []).push(w.message);
      } else if (w && w.bookId) {
        (details[k] = details[k] || []).push(`book #${w.bookId}`);
      } else if (w && w.draftId) {
        (details[k] = details[k] || []).push(`draft #${w.draftId}`);
      }
    }

    const parts: string[] = [];
    for (const k of Object.keys(counts)) {
      const label = kindLabels[k] || k;
      let part = `${label}: ${counts[k]}`;
      const ds = details[k];
      if (ds && ds.length) {
        // Join multiple detail messages but keep the summary short.
        part += ` (${ds.slice(0, 3).join('; ')})`;
      }
      parts.push(part);
    }

    return `Project created with warnings — ${parts.join(', ')}. See console for details.`;
  }

  close(): void {
    this.dialogRef.close();
  }
}




